import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { MapComponent } from '@/components/MapComponent';
import { ShareFlyer } from '@/components/ShareFlyer';
import { LogoIcon } from '@/components/Logo';
import FlyerModal from '@/components/FlyerModal';
import ShareButtons from '@/components/ShareButtons';
import CaseShareBar from '@/components/CaseShareBar';
import CaseGenealogySection from '@/components/CaseGenealogySection';
import CaseNetworkSection from '@/components/CaseNetworkSection';
import NamusImage from '@/components/NamusImage';
import { ImageWithFallback } from '@/components/OptimizedImage';
import { SEO, generateMissingPersonSchema } from '@/components/SEO';
import { LatestUpdateCard, UpdatesTimeline, KeyDevelopmentsChips } from '@/components/CaseUpdates';
import CaseOutreachHistory from '@/components/CaseOutreachHistory';
import CasePodcastCoverage from '@/components/CasePodcastCoverage';
import CaseInvestigatorNotes, {
  EnrichmentFlagBadges,
} from '@/components/CaseInvestigatorNotes';
import CaseReferences from '@/components/CaseReferences';
import AddCaseUpdateForm from '@/components/AddCaseUpdateForm';
import SourcesTab from '@/components/SourcesTab';
import CreateOutreachButton from '@/components/CreateOutreachButton';
import MarkIdentifiedDialog from '@/components/MarkIdentifiedDialog';
import MOExclusionDialog from '@/components/MOExclusionDialog';
import CaseDescriptionSection from '@/components/CaseDescriptionSection';
import InvestigationFlags from '@/components/InvestigationFlags';
import CaseFlagEditor from '@/components/CaseFlagEditor';
import PhotoManager from '@/components/PhotoManager';
import ManualEditPanel from '@/components/ManualEditPanel';
import ExtractedIntelligence from '@/components/ExtractedIntelligence';
import CommunityResearchNotes from '@/components/CommunityResearchNotes';
import FlagMatchButton from '@/components/FlagMatchButton';
import CaseWorkbenchTab from '@/components/CaseWorkbenchTab';
import { SignupCTA } from '@/components/SignupCTA';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CaseDetailNudge, useIsPro, useActionTracker, GentleUpgradeModal } from '@/components/UpgradeNudges';
import { useSubscription } from '@/context/SubscriptionContext';
import { SaveCaseButton } from '@/components/SaveCaseButton';
import { trackCaseViewed } from '@/lib/telemetry';
import { formatAge } from '@/utils/ageUtils';
import { formatDate } from '@/utils/dateUtils';
import { getCase, getAnyCase, getTipsForCase, getAISummary, findSimilarCases, findSimilarCasesEnhanced, getExternalCase, getExternalCaseLocation, trackShare, getShareCount, getMatchesForCase, generateAgeProgression, getCachedAgeProgression, reportAgeProgression, boostCaseToFeatured, getFeaturedCredits, getCaseTimeline, updateCaseFeaturedStatus } from '@/lib/api';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { 
  MapPin, Calendar, AlertTriangle, User, Phone, ArrowLeft,
  MessageCircle, Sparkles, Loader2, Clock, ExternalLink, Database,
  Scale, X, ChevronRight, Share2, Facebook, Twitter, Linkedin, Mail, Copy, Check,
  Link2, Lock, ArrowRight, Wand2, ImageIcon, Printer, Star, History, ChevronLeft, Edit, Plus, FileText, Camera, ArrowUpCircle, Search, Infinity, GitCompare, Network, Crown, Mic, CheckCircle2, ShieldAlert, Download, Target
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Clean up messy names from data imports
// Removes leading colons, state abbreviations in parentheses, extra whitespace
const cleanName = (name) => {
  if (!name) return 'Unknown';
  return name
    .replace(/^:\s*/, '')                    // Remove leading colon and space
    .replace(/\s*\([A-Z]{2}\)\s*$/, '')      // Remove state abbreviation at end like "(AZ)"
    .replace(/\s*\([A-Za-z\s]+\)\s*$/, '')   // Remove state name at end like "(Arizona)"
    .replace(/\s+/g, ' ')                     // Normalize whitespace
    .trim();
};

// Format name for unidentified/unclaimed cases to be more readable
const formatDisplayName = (caseData) => {
  if (!caseData) return 'Unknown';
  
  const name = cleanName(caseData.name);
  const caseType = caseData.case_type;
  const source = caseData.source;
  
  // Check if this is an unidentified or unclaimed case with a generic name
  const isUnidentified = caseType === 'unidentified' || 
                         caseType === 'unidentified_person' ||
                         source === 'namus_unidentified' ||
                         source === 'doenetwork';
  const isUnclaimed = caseType === 'unclaimed_person' || 
                      caseType === 'unclaimed' ||
                      source === 'namus_unclaimed';
  
  // Check if the name is generic (like "Unidentified Female", "Jane Doe", etc.)
  const genericPatterns = [
    /^unidentified\s+(male|female|person|individual|body|remains)/i,
    /^unidentified\s+(male|female),?\s*\d+/i, // NamUs style: "Unidentified Male, 40-99 years old"
    /^(john|jane)\s+doe$/i,
    /^unknown\s+(male|female|person)/i,
    /^(male|female)\s+unidentified$/i,
  ];
  
  const isGenericName = genericPatterns.some(pattern => pattern.test(name));
  
  if (isGenericName) {
    // Extract gender from the name if present
    const genderMatch = name.match(/\b(male|female)\b/i);
    const gender = genderMatch ? genderMatch[1].charAt(0).toUpperCase() + genderMatch[1].slice(1).toLowerCase() : null;
    
    // Extract age range if present (like "40-99 years old")
    const ageMatch = name.match(/(\d+[-–]\d+)\s*(?:years?\s*old)?/i);
    const ageRange = ageMatch ? ageMatch[1].replace('–', '-') : null;
    
    if (isUnclaimed) {
      let displayName = gender ? `Unclaimed ${gender}` : 'Unclaimed Person';
      if (ageRange) displayName += ` (${ageRange} yrs)`;
      return displayName;
    } else if (isUnidentified) {
      let displayName = gender ? `Unidentified ${gender}` : 'Unidentified Person';
      if (ageRange) displayName += ` (${ageRange} yrs)`;
      return displayName;
    }
  }
  
  return name;
};

// Helper function to simplify and prioritize match reasons for everyday users
const processMatchReasons = (reasons) => {
  if (!reasons || !Array.isArray(reasons)) return [];
  
  // Priority categories (higher = shown first)
  const getPriority = (reason) => {
    const lowerReason = reason.toLowerCase();
    if (lowerReason.includes('tattoo') && (lowerReason.includes('text') || lowerReason.includes('name'))) return 100;
    if (lowerReason.includes('clothing text') || lowerReason.includes('brand')) return 95;
    if (lowerReason.includes('scar') && lowerReason.includes('surgical')) return 90;
    if (lowerReason.includes('tattoo')) return 85;
    if (lowerReason.includes('scar') || lowerReason.includes('birthmark')) return 80;
    if (lowerReason.includes('dental')) return 75;
    if (lowerReason.includes('timeline')) return 65;
    if (lowerReason.includes('age within')) return 40;
    if (lowerReason.includes('race')) return 35;
    if (lowerReason.includes('gender')) return 10;
    if (lowerReason.includes('same state')) return 5;
    return 50;
  };
  
  // Simplify reason text
  const simplifyReason = (reason) => {
    let simplified = reason;
    simplified = simplified.replace(/\s*\(\+?\d+\)\s*/g, ' ').trim();
    simplified = simplified.replace(/^CLOTHING TEXT MATCH:\s*/i, '');
    simplified = simplified.replace(/\s*-\s*CRITICAL$/i, '');
    simplified = simplified.replace(/^DISTINCTIVE:\s*/i, '');
    
    if (simplified.toLowerCase().includes('matching text on clothing')) {
      const brandMatch = simplified.match(/'([^']+)'/);
      if (brandMatch) simplified = `Clothing brand: "${brandMatch[1]}"`;
    }
    
    if (simplified.toLowerCase().includes('scar match')) {
      simplified = simplified.replace(/\d+ moderate match\(es\)\s*-?\s*/i, '');
      simplified = simplified.replace(/same body region:\s*/i, 'Scars on ');
    }
    
    return simplified.replace(/\s+/g, ' ').trim();
  };
  
  return [...reasons]
    .map(reason => ({ simplified: simplifyReason(reason), priority: getPriority(reason) }))
    .sort((a, b) => b.priority - a.priority)
    .map(item => item.simplified);
};

export default function CaseDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isPro = useIsPro();
  const { trialAvailable, startTrial, isPro: isProSubscription } = useSubscription();
  const [caseData, setCaseData] = useState(null);
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiSummary, setAiSummary] = useState(null);
  const [similarCases, setSimilarCases] = useState([]);
  const [resolverMessage, setResolverMessage] = useState(null);
  const [distinctiveFeatures, setDistinctiveFeatures] = useState([]);
  const [loadingAI, setLoadingAI] = useState(false);
  const [isExternalCase, setIsExternalCase] = useState(false);
  const [shareCount, setShareCount] = useState(0);
  const [copiedLink, setCopiedLink] = useState(false);
  const [markIdentifiedOpen, setMarkIdentifiedOpen] = useState(false);
  const [moExclusionOpen, setMoExclusionOpen] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [potentialMatches, setPotentialMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesAccess, setMatchesAccess] = useState(null);
  const [totalMatchesAvailable, setTotalMatchesAvailable] = useState(0);
  const [matchesLimited, setMatchesLimited] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);
  const [ageProgressions, setAgeProgressions] = useState([]);
  const [ageProgressionLoading, setAgeProgressionLoading] = useState(false);
  const [ageProgressionAccess, setAgeProgressionAccess] = useState(null);
  const [showAgeProgression, setShowAgeProgression] = useState(false);
  const [showFlyerModal, setShowFlyerModal] = useState(false);
  // Boost to Featured state
  const [boostLoading, setBoostLoading] = useState(false);
  const [featuredCredits, setFeaturedCredits] = useState(null);
  
  // Extracted location state (for cases without coordinates)
  const [extractedLocation, setExtractedLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  
  // Timeline state
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  
  // Case Updates state
  const [caseUpdates, setCaseUpdates] = useState([]);
  const [latestUpdate, setLatestUpdate] = useState(null);
  const [updatesLoading, setUpdatesLoading] = useState(false);
  const [showAddUpdate, setShowAddUpdate] = useState(false);
  const [showAllUpdates, setShowAllUpdates] = useState(false);
  
  // Related Cases state
  const [relatedCases, setRelatedCases] = useState([]);
  const [relatedCasesLoading, setRelatedCasesLoading] = useState(false);
  
  // Extracted Intelligence state
  const [extractedIntel, setExtractedIntel] = useState([]);
  const [intelSources, setIntelSources] = useState([]);
  const [intelLoading, setIntelLoading] = useState(false);
  
  // Photo gallery state
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Workbench tab toggle (Reddit launch — Case Workbench MVP)
  const [activeTab, setActiveTab] = useState('overview');
  
  // Compare mode state - persist in localStorage
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState(() => {
    try {
      const stored = localStorage.getItem('compareCases');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Sync selectedForCompare to localStorage and dispatch custom event
  useEffect(() => {
    localStorage.setItem('compareCases', JSON.stringify(selectedForCompare));
    // Dispatch custom event for same-tab sync
    window.dispatchEvent(new CustomEvent('compareCasesUpdated', { 
      detail: selectedForCompare 
    }));
  }, [selectedForCompare]);

  // Listen for compare state changes from other components (same tab)
  useEffect(() => {
    const handleCompareUpdate = (e) => {
      setSelectedForCompare(e.detail || []);
    };
    
    // Also handle storage events from other tabs
    const handleStorageChange = (e) => {
      if (e.key === 'compareCases') {
        try {
          const parsed = e.newValue ? JSON.parse(e.newValue) : [];
          setSelectedForCompare(parsed);
        } catch {
          setSelectedForCompare([]);
        }
      }
    };
    
    window.addEventListener('compareCasesUpdated', handleCompareUpdate);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('compareCasesUpdated', handleCompareUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    fetchCaseData();
    if (user) {
      loadFeaturedCredits();
    }
  }, [id, user]);

  const loadFeaturedCredits = async () => {
    try {
      const credits = await getFeaturedCredits();
      setFeaturedCredits(credits);
    } catch (error) {
      console.log('Could not load featured credits');
    }
  };

  const handleBoostCase = async () => {
    if (!user) {
      toast.error('Please log in to feature cases');
      return;
    }
    
    if (!featuredCredits?.can_feature) {
      toast.error('Upgrade to Professional or Organization to feature cases');
      return;
    }
    
    if (featuredCredits.available_credits <= 0) {
      toast.error('No featured credits available');
      return;
    }
    
    setBoostLoading(true);
    try {
      const result = await boostCaseToFeatured(id);
      toast.success(result.message);
      // Refresh case data and credits
      fetchCaseData();
      loadFeaturedCredits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to feature case');
    } finally {
      setBoostLoading(false);
    }
  };

  // Scroll to merged sources section
  const scrollToMergedSources = () => {
    const element = document.getElementById('merged-sources-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const fetchCaseData = async () => {
    setLoading(true);
    try {
      // Use unified endpoint that searches all collections in one call
      const caseResult = await getAnyCase(id);

      if (!caseResult) {
        console.error('No case result returned');
        setCaseData(null);
        setLoading(false);
        return;
      }

      // Set external flag based on response from unified endpoint
      const isExternal = caseResult.is_external === true;
      setIsExternalCase(isExternal);
      setCaseData(caseResult);

      // Funnel telemetry — case_viewed (fires once per successful load)
      trackCaseViewed(id, {
        source: caseResult.source || null,
        case_type: caseResult.case_type || null,
        is_external: isExternal,
        is_canonical: Boolean(caseResult.canonical_id || caseResult.merged_from?.length),
      });
      
      // Fetch tips (only applicable for non-external cases, but try anyway)
      try {
        const tipsResult = await getTipsForCase(id);
        setTips(tipsResult || []);
      } catch (e) {
        setTips([]);
      }
      
      // Fetch share count
      try {
        const shareData = await getShareCount(id);
        setShareCount(shareData.share_count || 0);
      } catch (e) {
        setShareCount(caseResult.share_count || 0);
      }
      
      // Fetch AI summary for non-external cases
      if (!isExternal) {
        fetchAISummary();
      }
    } catch (error) {
      // Case not found by internal id. Try the case-number resolver —
      // `id` might be a NamUs #, Doe Network ID, or other public
      // identifier the user typed directly into the URL.
      try {
        const resolverResp = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/cases/resolve/${encodeURIComponent(id)}`,
        );
        if (resolverResp.ok) {
          const resolved = await resolverResp.json();
          if (resolved.status === 'resolved' && resolved.internal_id
              && resolved.internal_id !== id) {
            navigate(`/case/${resolved.internal_id}`, { replace: true });
            return;
          }
          if (resolved.status === 'ambiguous') {
            navigate(
              `/browse-cases?q=${encodeURIComponent(id)}&ambiguous=1`,
              { replace: true },
            );
            return;
          }
          if (resolved.status === 'not_found') {
            setResolverMessage(resolved.message);
          }
        }
      } catch (resolveErr) {
        console.error('Resolver error:', resolveErr);
      }
      console.error('Error fetching case:', error);
      setCaseData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchAISummary = async () => {
    try {
      const result = await getAISummary(id);
      setAiSummary(result.summary);
    } catch (error) {
      console.error('AI summary failed:', error);
    }
  };

  // Fetch case updates
  const fetchCaseUpdates = async () => {
    setUpdatesLoading(true);
    try {
      // Fetch latest update
      const latestResponse = await api.get(`/case-updates/latest/${id}`);
      setLatestUpdate(latestResponse.data.latest_update);
      
      // Fetch all updates
      const allResponse = await api.get(`/case-updates/case/${id}`);
      setCaseUpdates(allResponse.data.updates || []);
    } catch (error) {
      console.log('Could not load case updates:', error);
    } finally {
      setUpdatesLoading(false);
    }
  };

  // Fetch updates when case data is loaded
  useEffect(() => {
    if (caseData && id) {
      fetchCaseUpdates();
      fetchRelatedCases();
      fetchExtractedIntel();
    }
  }, [caseData, id]);

  // Emit a per-case <meta og:image> / <meta twitter:image> / <title>
  // override when a case loads. Restores global defaults on unmount so
  // social-preview crawls of other routes still resolve correctly.
  useEffect(() => {
    if (!id) return undefined;
    const backendBase = process.env.REACT_APP_BACKEND_URL || '';
    // Use the public canonical URL so crawlers follow it.
    const shareUrl = `https://linkthemissing.com/case/${id}`;
    const ogImage = `${backendBase}/api/case/${id}/og.png`;
    const displayName = (() => {
      if (!caseData) return null;
      const n = (caseData.name || '').trim();
      if (n && !n.toLowerCase().startsWith('unidentified')) {
        return n.replace(/^[:\s]+/, '');
      }
      const sex = caseData.sex || caseData.gender || 'Person';
      const amin = caseData.age_min, amax = caseData.age_max;
      if (amin && amax && amin !== amax) return `Unidentified ${sex}, age ${amin}–${amax}`;
      if (caseData.age) return `Unidentified ${sex}, age ${caseData.age}`;
      return `Unidentified ${sex}`;
    })();
    const title = displayName
      ? `${displayName} — LinkTheMissing`
      : 'LinkTheMissing — Every Missing Person Deserves to Be Found';
    const descParts = [];
    if (caseData?.namus_number) descParts.push(`NamUs ${caseData.namus_number}`);
    if (caseData?.city || caseData?.state) {
      descParts.push([caseData.city, caseData.state].filter(Boolean).join(', '));
    }
    const desc = displayName
      ? `${displayName}${descParts.length ? ' · ' + descParts.join(' · ') : ''}. Help identify this person — LinkTheMissing.`
      : null;

    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const [key, val] = selector.replace(/^meta\[|\]$/g, '').split('=');
        el.setAttribute(key, val.replace(/['"]/g, ''));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    const prev = {
      title: document.title,
      ogTitle: document.head.querySelector('meta[property="og:title"]')?.getAttribute('content'),
      ogImage: document.head.querySelector('meta[property="og:image"]')?.getAttribute('content'),
      ogUrl: document.head.querySelector('meta[property="og:url"]')?.getAttribute('content'),
      ogDesc: document.head.querySelector('meta[property="og:description"]')?.getAttribute('content'),
      twImage: document.head.querySelector('meta[name="twitter:image"]')?.getAttribute('content'),
      twTitle: document.head.querySelector('meta[name="twitter:title"]')?.getAttribute('content'),
      twDesc: document.head.querySelector('meta[name="twitter:description"]')?.getAttribute('content'),
    };

    document.title = title;
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:image"]', 'content', ogImage);
    setMeta('meta[property="og:image:secure_url"]', 'content', ogImage);
    setMeta('meta[property="og:url"]', 'content', shareUrl);
    setMeta('meta[name="twitter:image"]', 'content', ogImage);
    setMeta('meta[name="twitter:title"]', 'content', title);
    if (desc) {
      setMeta('meta[property="og:description"]', 'content', desc);
      setMeta('meta[name="twitter:description"]', 'content', desc);
    }

    return () => {
      // Restore globals when leaving the case page
      document.title = prev.title;
      if (prev.ogTitle) setMeta('meta[property="og:title"]', 'content', prev.ogTitle);
      if (prev.ogImage) setMeta('meta[property="og:image"]', 'content', prev.ogImage);
      if (prev.ogUrl) setMeta('meta[property="og:url"]', 'content', prev.ogUrl);
      if (prev.ogDesc) setMeta('meta[property="og:description"]', 'content', prev.ogDesc);
      if (prev.twImage) setMeta('meta[name="twitter:image"]', 'content', prev.twImage);
      if (prev.twTitle) setMeta('meta[name="twitter:title"]', 'content', prev.twTitle);
      if (prev.twDesc) setMeta('meta[name="twitter:description"]', 'content', prev.twDesc);
    };
  }, [id, caseData]);

  // Fetch extracted intelligence for this case
  const fetchExtractedIntel = async () => {
    setIntelLoading(true);
    try {
      // Fetch sources for this case
      const sourcesResponse = await fetch(`${API}/api/intel/sources/${id}`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (sourcesResponse.ok) {
        const sourcesData = await sourcesResponse.json();
        setIntelSources(sourcesData.sources || []);
      }
      
      // Fetch intel items for this case
      // For non-admin users, only show approved items
      const isAdmin = user?.is_admin;
      const itemsUrl = isAdmin 
        ? `${API}/api/intel/items/${id}` 
        : `${API}/api/intel/items/${id}?status=approved`;
      
      const itemsResponse = await fetch(itemsUrl, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (itemsResponse.ok) {
        const itemsData = await itemsResponse.json();
        setExtractedIntel(itemsData.items || []);
      }
    } catch (error) {
      console.log('Could not load extracted intelligence:', error);
    } finally {
      setIntelLoading(false);
    }
  };

  // Fetch related cases (people who went missing together, etc.)
  const fetchRelatedCases = async () => {
    setRelatedCasesLoading(true);
    try {
      const response = await fetch(`${API}/api/related-cases/${id}`);
      if (response.ok) {
        const data = await response.json();
        setRelatedCases(data.related_cases || []);
      }
    } catch (error) {
      console.log('Could not load related cases:', error);
    } finally {
      setRelatedCasesLoading(false);
    }
  };

  // Fetch extracted location when case doesn't have coordinates
  const fetchExtractedLocation = async () => {
    if (!caseData || (caseData.latitude && caseData.longitude)) {
      return; // Already has coordinates
    }
    
    setLocationLoading(true);
    try {
      const result = await getExternalCaseLocation(id);
      if (result.has_coordinates) {
        setExtractedLocation({
          latitude: result.latitude,
          longitude: result.longitude,
          locationName: result.location_name || result.display_name,
          source: result.source
        });
      }
    } catch (error) {
      console.error('Failed to extract location:', error);
    } finally {
      setLocationLoading(false);
    }
  };

  const fetchPotentialMatches = async () => {
    setMatchesLoading(true);
    try {
      const result = await getMatchesForCase(id);
      setMatchesAccess(result.access_level);
      if (result.matches) {
        setPotentialMatches(result.matches);
      }
      // Track subscription limiting info
      if (result.subscription_limited) {
        setMatchesLimited(true);
        setTotalMatchesAvailable(result.total_available || result.matches?.length || 0);
      } else {
        setMatchesLimited(false);
        setTotalMatchesAvailable(result.matches?.length || 0);
      }
    } catch (error) {
      console.error('Failed to fetch potential matches:', error);
    } finally {
      setMatchesLoading(false);
    }
  };

  // Fetch potential matches when case loads (for external cases which are in the matching system)
  useEffect(() => {
    if (caseData && isExternalCase) {
      fetchPotentialMatches();
    }
  }, [caseData, isExternalCase]);

  // Fetch extracted location when case loads but has no coordinates
  useEffect(() => {
    if (caseData && !caseData.latitude && !caseData.longitude) {
      fetchExtractedLocation();
    }
  }, [caseData]);

  // Fetch cached age progressions when case loads
  const fetchCachedAgeProgressions = async () => {
    try {
      const result = await getCachedAgeProgression(id);
      setAgeProgressionAccess(result.access_level);
      if (result.progressions && result.progressions.length > 0) {
        setAgeProgressions(result.progressions);
        setShowAgeProgression(true);
      }
    } catch (error) {
      console.error('Failed to fetch cached age progressions:', error);
    }
  };

  useEffect(() => {
    if (caseData) {
      fetchCachedAgeProgressions();
    }
  }, [caseData]);

  // Fetch timeline events
  useEffect(() => {
    const fetchTimeline = async () => {
      if (!caseData) return;
      setTimelineLoading(true);
      try {
        const result = await getCaseTimeline(id);
        setTimelineEvents(result.events || []);
      } catch (error) {
        console.log('No timeline events found');
      } finally {
        setTimelineLoading(false);
      }
    };
    
    if (caseData) {
      fetchTimeline();
    }
  }, [caseData, id]);

  // Compute the dynamic age-progression intervals.
  //
  // Goal: instead of always firing [+5, +10, +15], fan out the
  // projections across the actual years-since-disappearance window so
  // a 2005 case gets ~[+7, +14, +21] and a 1985 case gets ~[+13, +27,
  // +40]. Falls back to [5, 10, 15] for very recent cases (<3 years
  // missing) where the legacy schedule still carries useful signal.
  //
  // Caps: each interval ≥1 yr; max single interval 50 yr to keep the
  // image-gen prompt grounded. Returns the original "last seen" year
  // alongside so the UI can render labels like "Last seen 2005 →
  // Estimated 2026".
  const computeProgressionPlan = () => {
    const lastSeenStr = caseData?.last_seen_date
      || caseData?.missing_since
      || caseData?.date_missing
      || caseData?.date_found;
    const fallback = { years: [5, 10, 15], lastSeenYear: null, totalYearsMissing: null };
    if (!lastSeenStr) return fallback;
    let lastSeenDate;
    try {
      lastSeenDate = new Date(lastSeenStr);
      if (isNaN(lastSeenDate.getTime())) return fallback;
    } catch {
      return fallback;
    }
    const lastSeenYear = lastSeenDate.getFullYear();
    const currentYear = new Date().getFullYear();
    const totalYearsMissing = currentYear - lastSeenYear;
    if (totalYearsMissing < 3) {
      return { years: [5, 10, 15], lastSeenYear, totalYearsMissing };
    }
    const cap = (n) => Math.max(1, Math.min(50, Math.round(n)));
    const oneThird = cap(totalYearsMissing / 3);
    const twoThirds = cap((totalYearsMissing * 2) / 3);
    const full = cap(totalYearsMissing);
    // Dedupe + ascending
    const years = Array.from(new Set([oneThird, twoThirds, full]))
      .sort((a, b) => a - b);
    return { years, lastSeenYear, totalYearsMissing };
  };

  const handleGenerateAgeProgression = async () => {
    setAgeProgressionLoading(true);
    try {
      const plan = computeProgressionPlan();
      const result = await generateAgeProgression(id, plan.years);
      setAgeProgressionAccess(result.access_level);
      if (result.progressions) {
        setAgeProgressions(result.progressions);
        setShowAgeProgression(true);
        const failedCount = (result.progressions || []).filter(
          (p) => p.failure_code === 'SKIN_TONE_MISMATCH'
        ).length;
        if (failedCount > 0) {
          toast.warning(
            `${failedCount} of ${result.progressions.length} renders rejected for skin-tone mismatch. ` +
            'Click "Regenerate" to retry — the prompt will retry with the same identity constraints.',
            { duration: 8000 }
          );
        } else {
          toast.success('Age progressions generated successfully!');
        }
      } else if (result.message) {
        toast.info(result.message);
      }
    } catch (error) {
      console.error('Age progression error:', error);
      toast.error('Failed to generate age progressions. Please try again.');
    } finally {
      setAgeProgressionLoading(false);
    }
  };

  // Reporting an inaccurate progression. `reason` is one of:
  //   'race_mismatch' | 'skin_tone' | 'other_identity'
  //   → triggers cache delete + regenerate on next view.
  //   'low_quality' | 'unsure' → admin review only, image stays.
  const handleReportProgression = async (reason, yearsAdded = null) => {
    try {
      const res = await reportAgeProgression(id, {
        years_added: yearsAdded,
        reason,
      });
      toast.success(res.message || 'Reported. Thank you.');
      // If the cache was cleared, drop the rendered set so the user
      // sees the empty-state CTA and can click Regenerate.
      if (res.action === 'cache_cleared') {
        setAgeProgressions([]);
        setShowAgeProgression(false);
      }
    } catch (err) {
      console.error('Report progression error:', err);
      toast.error('Could not submit report. Please try again.');
    }
  };

  // Download a single age-progression render as a local PNG. We hit
  // the data: URL directly via an anchor element so the browser
  // dispatches its native download flow without needing a backend
  // round-trip. Filename includes the case's display name + how many
  // years the progression represents so a family can save multiple
  // variants without overwriting each other.
  const handleDownloadProgression = (prog) => {
    if (!prog?.image_base64) return;
    try {
      const base = (formatDisplayName(caseData) || 'case')
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40) || 'case';
      const yearsLabel = prog.years_added != null ? `+${prog.years_added}yr` : 'progression';
      const fname = `${base}-age-progression-${yearsLabel}.png`;
      const a = document.createElement('a');
      a.href = prog.image_base64;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Saved ${fname}`);
    } catch (err) {
      console.error('Download progression error:', err);
      toast.error('Could not save image. Try again or right-click → Save.');
    }
  };

  const handleFindSimilar = async () => {
    if (!id) return;
    setLoadingAI(true);
    try {
      // Use enhanced similarity search that matches on demographics + distinctive features
      const result = await findSimilarCasesEnhanced(id, 20);
      
      if (result.error) {
        toast.error(result.error);
        return;
      }
      
      setSimilarCases(result.similar_cases || []);
      setDistinctiveFeatures(result.source_case?.distinctive_features || []);
      
      if (result.similar_cases?.length === 0) {
        toast.info('No similar cases found matching the criteria');
      } else {
        toast.success(`Found ${result.similar_cases.length} potential matches`);
        // Scroll the first-visible results panel into view so the user sees
        // the output on both mobile (in-flow card) and desktop (sidebar card).
        setTimeout(() => {
          const target = document.querySelector(
            '[data-testid="find-similar-results"]:not(.hidden)',
          ) || document.querySelector('[data-testid="find-similar-results"]');
          target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      }
    } catch (error) {
      console.error('Similar cases error:', error);
      toast.error('Failed to find similar cases');
    } finally {
      setLoadingAI(false);
    }
  };

  const handleShare = async (platform) => {
    // Social-share crawlers must hit `/api/c/{id}` so they reach the
    // backend SSR meta-tag route (Emergent's ingress only proxies
    // `/api/*` to FastAPI; bare `/case/{id}` returns the static SPA
    // shell with generic OG tags). The route 302-redirects humans back
    // to `/case/{id}`. `window.location.href` would emit the in-browser
    // `/case/{id}` URL → broken card.
    const shareUrl = `${window.location.origin}/api/c/${id}`;
    const displayName = formatDisplayName(caseData);
    
    // Determine case type for appropriate share text
    const isUnidentified = caseData.case_type === 'unidentified' || 
                           caseData.case_type === 'unidentified_person' ||
                           caseData.source === 'namus_unidentified' ||
                           caseData.source === 'doenetwork';
    const isUnclaimed = caseData.case_type === 'unclaimed_person' || 
                        caseData.case_type === 'unclaimed' ||
                        caseData.source === 'namus_unclaimed';
    
    let shareTitle, shareText;
    if (isUnidentified) {
      shareTitle = `Help Identify: ${displayName}`;
      shareText = `Can you help identify this person? Found in ${caseData.state || 'unknown location'}.`;
    } else if (isUnclaimed) {
      shareTitle = `Unclaimed Person: ${displayName}`;
      shareText = `Help find the family of this unclaimed person. Found in ${caseData.state || 'unknown location'}.`;
    } else {
      shareTitle = `Missing: ${displayName}`;
      shareText = `Help find ${displayName}. Last seen at ${caseData.last_seen_location || caseData.state || 'unknown location'}.`;
    }
    
    // Track the share
    try {
      const result = await trackShare(id, platform);
      setShareCount(result.share_count);
    } catch (e) {
      console.error('Failed to track share:', e);
    }
    
    // Perform the share action
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400');
        break;
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl);
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
        toast.success('Link copied to clipboard!');
        break;
      default:
        if (navigator.share) {
          navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
        }
    }
    
    setShowShareMenu(false);
  };

  // Use centralized date formatting - now imported from utils/dateUtils
  const formatDisplayDate = (dateStr) => {
    return formatDate(dateStr, { fallback: 'Unknown' });
  };

  const placeholderImages = [
    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiB2aWV3Qm94PSIwIDAgNDAwIDQwMCI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSI0MDAiIGZpbGw9IiNmNWY1ZjUiLz48Y2lyY2xlIGN4PSIyMDAiIGN5PSIxNDAiIHI9IjcwIiBmaWxsPSIjZDFkNWRiIi8+PHBhdGggZD0iTTgwIDQwMHYtNDBjMC02Ni4zIDUzLjctMTIwIDEyMC0xMjBoMGM2Ni4zIDAgMTIwIDUzLjcgMTIwIDEyMHY0MCIgZmlsbD0iI2QxZDVkYiIvPjwvc3ZnPg==',
  ];

  // Check if URL is a NamUs default placeholder (blue silhouette)
  const isNamusPlaceholder = (url) => {
    if (!url) return true;
    // NamUs default images contain these patterns
    return url.includes('/Images/Default/') || url.includes('Thumbnail');
  };

  // Convert HTTP to HTTPS for security (prevents mixed content blocking)
  const ensureHttps = (url) => {
    if (!url) return url;
    return url.replace(/^http:\/\//i, 'https://');
  };

  // Open a photo in a new tab. Works for ordinary HTTPS URLs AND for
  // base64 data: URIs we generate when admins upload via PhotoManager.
  //
  // Why the helper exists: Chrome (and every modern browser since 2017)
  // BLOCKS top-level navigation to `data:` URIs as an anti-phishing
  // measure. Passing one to `window.open(uri, '_blank')` opens a tab
  // that stays blank with `about:blank` in the address bar — this is
  // the "click photo, new page is empty" bug reported on preview
  // 2026-05-14 after admins uploaded photos to cases.
  //
  // The workaround is to convert the data: URI into a Blob URL (which
  // browsers DO allow in `_blank` tabs), or to drop the URI into a
  // tiny rendered HTML document that's served by the opened tab itself.
  // We use the Blob approach because it's a single image render in the
  // tab, mirroring exactly what an HTTPS URL would do.
  const openPhoto = (photoSrc) => {
    if (!photoSrc) return;
    if (!photoSrc.startsWith('data:')) {
      // Ordinary HTTPS URL — straight through.
      window.open(photoSrc, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      const [meta, b64] = photoSrc.split(',');
      const mime = (meta.match(/data:([^;]+)/) || [])[1] || 'image/jpeg';
      const bytes = atob(b64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: mime });
      const blobUrl = URL.createObjectURL(blob);
      const opened = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      // Best-effort cleanup so we don't leak the blob URL forever. We
      // can't await the tab closing reliably, but revoking after 60s
      // covers the realistic "look at the photo, close the tab" flow
      // and well exceeds the time needed for the browser to render it.
      setTimeout(() => { try { URL.revokeObjectURL(blobUrl); } catch (_) {} }, 60_000);
      if (!opened) {
        // Pop-up blocker swallowed it. Toast a friendly hint rather
        // than another blank-tab regression.
        toast.error('Browser blocked the new tab — allow pop-ups and try again.');
      }
    } catch (e) {
      console.error('Failed to open data URI photo:', e);
      toast.error('Could not open photo — please refresh and try again.');
    }
  };

  const getImage = () => {
    // Prioritize base64 data when available (more reliable than URLs that may 404)
    // Check both photo_base64 (external cases) and primary_photo_base64 (canonical cases)
    const base64 = caseData?.photo_base64 || caseData?.primary_photo_base64;
    if (base64) {
      return base64.startsWith('data:') 
        ? base64 
        : `data:image/jpeg;base64,${base64}`;
    }
    // External case with photo_url
    if (caseData?.photo_url && !isNamusPlaceholder(caseData.photo_url)) {
      const url = caseData.photo_url;
      
      // Check if photo_url is already base64 data (NamUs unclaimed stores images this way)
      if (url.startsWith('data:image')) {
        return url;
      }
      
      // NamUs images require proxy due to CORS/headers
      if (url.includes('namus.gov') && url.includes('/Images/')) {
        // Extract case ID, image ID, and case type from URL
        // Format 1: .../CaseSets/NamUs/{CaseType}/Cases/{caseId}/Images/{imageId}/...
        // Format 2: .../CaseSets/NamUs/{CaseType}/Cases/{caseId}/Images/Default/...
        const match = url.match(/CaseSets\/NamUs\/(MissingPersons|UnclaimedPersons|UnidentifiedPersons)\/Cases\/(\d+)\/Images\/(\d+|Default)/);
        if (match) {
          const [, caseType, caseId, imageId] = match;
          // Use proxy endpoint with case_type parameter
          return `${API}/api/external/namus-image/${caseId}/${imageId}?size=Original&case_type=${caseType}`;
        }
        // Fallback for older URL format without case type
        const fallbackMatch = url.match(/Cases\/(\d+)\/Images\/(\d+|Default)/);
        if (fallbackMatch) {
          const [, caseId, imageId] = fallbackMatch;
          // Detect case type from caseData.source
          let caseType = 'MissingPersons';
          if (caseData?.source === 'namus_unclaimed') caseType = 'UnclaimedPersons';
          else if (caseData?.source === 'namus_unidentified') caseType = 'UnidentifiedPersons';
          return `${API}/api/external/namus-image/${caseId}/${imageId}?size=Original&case_type=${caseType}`;
        }
      }
      
      return ensureHttps(url);
    }
    return placeholderImages[0];
  };

  const getLocation = () => {
    if (caseData?.last_seen_location) return caseData.last_seen_location;
    if (caseData?.state) return caseData.state;
    return 'Location unknown';
  };

  const toggleCompareCase = (caseItem) => {
    if (selectedForCompare.find(c => c.id === caseItem.id)) {
      setSelectedForCompare(prev => prev.filter(c => c.id !== caseItem.id));
    } else if (selectedForCompare.length < 3) {
      setSelectedForCompare(prev => [...prev, caseItem]);
    } else {
      toast.error('You can compare up to 3 cases at a time');
    }
  };

  const addCurrentToCompare = () => {
    if (!caseData) return;
    if (selectedForCompare.find(c => c.id === caseData.id)) {
      toast.info('This case is already selected');
      return;
    }
    if (selectedForCompare.length >= 3) {
      toast.error('You can compare up to 3 cases at a time');
      return;
    }
    setSelectedForCompare(prev => [...prev, caseData]);
    setCompareMode(true);
    toast.success('Case added to compare. Find more cases to compare!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <LogoIcon size={64} className="animate-pulse mb-4" />
        <p className="text-muted-foreground">Loading case details...</p>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="p-8 text-center max-w-lg" data-testid="case-not-found">
          <LogoIcon size={48} className="mx-auto mb-6 opacity-50" />
          <h2 className="font-heading text-2xl font-bold mb-4">
            {resolverMessage ? 'Case Not Found' : 'Case Not Available'}
          </h2>
          <p className="text-muted-foreground mb-3">
            {resolverMessage || (
              <>This case is no longer active in our database.
              In many situations, this means the individual may have been located,
              the case was resolved, or official records were updated.</>
            )}
          </p>
          {!resolverMessage && (
            <p className="text-muted-foreground mb-6">
              We continuously update records to reflect the most current information available.
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/explore">
              <Button className="rounded-full w-full sm:w-auto">Explore Active Cases</Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="rounded-full w-full sm:w-auto">Report an Issue</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // SEO description for missing person
  const seoDisplayName = formatDisplayName(caseData);
  const isUnidentifiedCase = caseData.case_type === 'unidentified' || 
                             caseData.case_type === 'unidentified_person' ||
                             caseData.source === 'namus_unidentified' ||
                             caseData.source === 'doenetwork';
  const isUnclaimedCase = caseData.case_type === 'unclaimed_person' || 
                          caseData.case_type === 'unclaimed' ||
                          caseData.source === 'namus_unclaimed';
  
  let seoDescription;
  if (isUnidentifiedCase) {
    seoDescription = `Help identify this person. ${caseData.gender ? `${caseData.gender}.` : ''} Found in ${caseData.state || 'unknown location'}. Submit anonymous tips.`;
  } else if (isUnclaimedCase) {
    seoDescription = `Help identify the family of this unclaimed person. ${caseData.gender ? `${caseData.gender}.` : ''} Found in ${caseData.state || 'unknown location'}.`;
  } else {
    seoDescription = `Help find ${seoDisplayName}. ${caseData.age ? `Age ${caseData.age}.` : ''} ${caseData.last_seen_location ? `Last seen in ${caseData.last_seen_location}.` : ''} Submit anonymous tips.`;
  }

  // Shared results block — rendered inside BOTH the desktop sidebar card
  // and the mobile in-flow card so clicking the button always produces
  // visible output regardless of viewport.
  const isUidLikeCaseType = (
    caseData.case_type === 'unidentified' ||
    caseData.case_type === 'unidentified_person' ||
    caseData.source === 'namus_unidentified' ||
    caseData.source === 'doenetwork' ||
    caseData.case_type === 'unclaimed_person' ||
    caseData.case_type === 'unclaimed' ||
    caseData.source === 'namus_unclaimed'
  );
  // MatchThumb — small (~64x80) photo for match preview cards. Renders a
  // neutral SVG silhouette placeholder when photoUrl is null/empty or when
  // the upstream image fails to load. Used in both the free-tier "Top 3
  // Matches" preview and the Pro-tier renderSimilarResultsBlock.
  const MATCH_THUMB_PLACEHOLDER =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="80" viewBox="0 0 64 80">
         <rect width="64" height="80" fill="#e2e8f0"/>
         <circle cx="32" cy="30" r="12" fill="#94a3b8"/>
         <path d="M12 70 C 12 54, 52 54, 52 70 L 52 80 L 12 80 Z" fill="#94a3b8"/>
       </svg>`
    );

  const MatchThumb = ({ photoUrl, alt }) => {
    if (!photoUrl) {
      return (
        <img
          src={MATCH_THUMB_PLACEHOLDER}
          alt={alt || 'No photo available'}
          className="w-16 h-20 rounded-md object-cover bg-slate-100 dark:bg-slate-800 shrink-0 border border-slate-200 dark:border-slate-700"
          aria-hidden="true"
        />
      );
    }
    return (
      <ImageWithFallback
        src={photoUrl}
        fallback={MATCH_THUMB_PLACEHOLDER}
        alt={alt || 'Match photo'}
        className="w-16 h-20 rounded-md object-cover bg-slate-100 dark:bg-slate-800 shrink-0 border border-slate-200 dark:border-slate-700"
      />
    );
  };

  const renderSimilarResultsBlock = () => (
    <div data-testid="find-similar-results">
      {distinctiveFeatures.length > 0 && (
        <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
          <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" />
            Distinctive Features Identified
          </h4>
          <div className="space-y-1">
            {distinctiveFeatures.map((feature, idx) => (
              <div key={idx} className="text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
                <Badge variant="outline" className="shrink-0 text-[10px] bg-amber-100 dark:bg-amber-900/50 border-amber-300">
                  {feature.type === 'clothing_mismatch' ? 'UNUSUAL' : feature.type.toUpperCase()}
                </Badge>
                <span>{feature.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {similarCases.length > 0 && (
        <div className="mt-4 space-y-3">
          <div>
            <h4 className="text-sm font-semibold text-muted-foreground">
              {similarCases.length} {isUidLikeCaseType ? 'Potential Missing Person Matches' : 'Potential Unidentified Matches'}
            </h4>
            {/* Scores below mirror the Matches page V2 engine
                (0-100 match_score). Same algorithm, same scale. */}
            <p className="text-[11px] text-muted-foreground mt-1 italic">
              Same algorithm and 0–100 match score as the
              <Link to="/matches" className="underline ml-1 text-indigo-600">
                Matches page
              </Link>
              .
            </p>
          </div>
          {/* Admin-only one-click draft path. Picks the top match
              (similarCases[0]) and deep-links into the AdminOutreachQueue
              create modal with the case-pair-table template pre-fired. */}
          {user?.is_admin && similarCases[0] && (
            <div className="p-3 border border-indigo-300 bg-indigo-50 dark:bg-indigo-950/30 dark:border-indigo-700 rounded-md flex items-start gap-3"
                 data-testid="case-detail-draft-top-match-banner">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-indigo-900 dark:text-indigo-200">
                  Draft an outreach letter for the top match
                </div>
                <div className="text-[11px] text-indigo-800 dark:text-indigo-300 leading-snug mt-0.5 truncate">
                  Top: <span className="font-medium">{similarCases[0].name || 'Unidentified record'}</span>{' '}
                  · {Math.round(similarCases[0].match_score ?? similarCases[0].score ?? 0)} / 100
                  · uses the case-pair-table template
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => {
                  const top = similarCases[0];
                  if (!top?.case_id) {
                    toast.error('Top match has no case_id');
                    return;
                  }
                  // The "this" side is the case being viewed; the other side is
                  // similarCases[0]. The backend resolver figures out which is
                  // the MP and which is the UID — we don't need to disambiguate
                  // here. Letter path uses both ids' first 8 chars so two
                  // different pair-attempts on the same MP don't collide.
                  const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '_');
                  const aShort = (id || '').slice(0, 8);
                  const bShort = (top.case_id || '').slice(0, 8);
                  const letterPath = `/app/backend/content/outreach_${aShort}_${bShort}_${stamp}.md`;
                  const caseIds = `${id} ${top.case_id}`;
                  const qs = new URLSearchParams({
                    createOpen: '1',
                    template: 'case_pair_with_tables',
                    letter_path: letterPath,
                    case_ids: caseIds,
                    autogen: '1',
                  });
                  navigate(`/admin/outreach?${qs.toString()}`);
                }}
                disabled={!similarCases[0]?.case_id}
                className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
                data-testid="case-detail-draft-top-match-button">
                <Mail className="w-3 h-3 mr-1" /> Draft letter
              </Button>
            </div>
          )}
          {similarCases.map((similar) => (
            <Link to={`/case/${similar.case_id}`} key={similar.case_id} data-testid={`similar-case-${similar.case_id}`}>
              <Card className="p-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer">
                <div className="flex gap-3">
                  <MatchThumb photoUrl={similar.photo_url} alt={similar.name} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <span className="font-medium text-sm truncate">{similar.name}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge
                          variant="secondary"
                          className={`text-xs ${
                            similar.tier === 'high' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300' :
                            similar.tier === 'strong' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300' :
                            similar.tier === 'possible' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' :
                            'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {similar.tier === 'high' ? 'Strong' :
                           similar.tier === 'strong' ? 'Moderate' :
                           similar.tier === 'possible' ? 'Preliminary' : 'Low'}
                        </Badge>
                        <span
                          className="text-xs font-mono text-muted-foreground"
                          title="V2 match score (0–100) — same as Matches page.">
                          {Math.round(similar.match_score ?? similar.score ?? 0)} / 100
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground mb-2">
                      {similar.state && <span className="mr-2">{similar.state}</span>}
                      {similar.age_range && similar.age_range !== '?-?' && <span className="mr-2">Age: {similar.age_range}</span>}
                      {similar.date_found && <span>Found: {formatDate(similar.date_found)}</span>}
                    </div>
                    {similar.confidence != null && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${similar.confidence >= 0.8 ? 'bg-green-500' : similar.confidence >= 0.5 ? 'bg-amber-500' : 'bg-slate-400'}`}
                            style={{ width: `${similar.confidence * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {Math.round(similar.confidence * 100)}% data
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen py-8 lg:py-12">
      <SEO
        title={`Missing: ${seoDisplayName}`}
        description={seoDescription}
        image={caseData.photo_url}
        url={`/case/${id}`}
        type="article"
        schema={generateMissingPersonSchema(caseData)}
      />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button - Smart navigation based on where user came from */}
        <button 
          onClick={() => {
            // Check if we have navigation history to go back to
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              // Fallback to browse if no history
              navigate('/browse');
            }
          }}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {/* Workbench / Overview tabs (Case Workbench MVP — Reddit launch) */}
        <div className="mb-6" data-testid="case-detail-tabs">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 sm:w-auto sm:inline-flex">
              <TabsTrigger value="overview" data-testid="tab-overview">
                Overview
              </TabsTrigger>
              <TabsTrigger value="workbench" data-testid="tab-workbench">
                Workbench
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {activeTab === 'workbench' && (
          <CaseWorkbenchTab
            caseData={caseData}
            caseId={id}
            matches={potentialMatches}
          />
        )}

        {activeTab === 'overview' && (
        <>

        {/* Compare Mode Banner */}
          {selectedForCompare.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
            >
              <Card className="bg-purple-50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <Scale className="w-5 h-5 text-purple-600" />
                    <span className="font-medium text-purple-700 dark:text-purple-400">
                      {selectedForCompare.length} case{selectedForCompare.length > 1 ? 's' : ''} selected for comparison
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedForCompare.map(c => (
                      <Badge key={c.id} variant="secondary" className="flex items-center gap-1">
                        {c.name?.split(' ')[0]}
                        <button onClick={() => toggleCompareCase(c)} className="ml-1 hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {selectedForCompare.length >= 2 && (
                      <Link to={`/compare?ids=${selectedForCompare.map(c => c.id).join(',')}`}>
                        <Button size="sm" className="bg-purple-500 hover:bg-purple-600 text-white rounded-full">
                          Compare Now
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => {
                        setSelectedForCompare([]);
                        localStorage.removeItem('compareCases');
                        window.dispatchEvent(new CustomEvent('compareCasesUpdated', { detail: [] }));
                      }}
                      className="text-purple-600"
                    >
                      Clear All
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="overflow-hidden" data-testid="case-detail-card">
                {/* items-start stops the photo cell from stretching to
                    match the right column when investigator notes /
                    references grow the page. Photo keeps its natural
                    aspect ratio; right column is free to grow
                    independently. */}
                <div className="grid md:grid-cols-2 gap-0 md:items-start">
                  {/* Photo — locked to square so the right column's
                      growth never distorts the image's aspect ratio. */}
                  <div className="relative aspect-square bg-gray-100 dark:bg-gray-800 md:self-start">
                    {(() => {
                      // Build array of all photos - prioritize base64 data for reliability
                      const allPhotos = [];
                      const mainPhoto = caseData.photo_base64 || caseData.primary_photo_base64 || caseData.photo_url;
                      if (mainPhoto) allPhotos.push(mainPhoto);
                      if (caseData.additional_photos) {
                        caseData.additional_photos.forEach(p => {
                          if (typeof p === 'string') allPhotos.push(p);
                          else if (p.base64) allPhotos.push(p.base64);
                          else if (p.url) allPhotos.push(p.url);
                          else if (p.photo_url) allPhotos.push(p.photo_url);
                        });
                      }
                      // 2026-06-01 fix: also walk all_photo_urls and photos[]
                      // (flat string arrays used by NamUs unidentified/unclaimed
                      // and TXDPS/CA-DOJ/CO-CBI/VA-VSP respectively). Without
                      // this the carousel arrows never appear on TXDPS-style
                      // multi-photo records because allPhotos.length stays at 1.
                      // Dedupe URLs so we don't add the main twice when it's
                      // also the first element of a flat-string array.
                      const seen = new Set(allPhotos.filter(Boolean));
                      const _pushUnique = (urls) => {
                        if (!Array.isArray(urls)) return;
                        urls.forEach(u => {
                          if (u && !seen.has(u)) {
                            allPhotos.push(u);
                            seen.add(u);
                          }
                        });
                      };
                      _pushUnique(caseData.all_photo_urls);
                      _pushUnique(caseData.photos);
                      
                      const hasMultiplePhotos = allPhotos.length > 1;
                      const currentPhoto = allPhotos[currentPhotoIndex] || getImage();
                      
                      return (
                        <>
                          <NamusImage
                            src={currentPhoto}
                            alt={caseData.name}
                            className="w-full h-full object-cover"
                            fallbackSrc={placeholderImages[0]}
                            showLoader={true}
                          />
                          
                          {/* Photo navigation arrows */}
                          {hasMultiplePhotos && (
                            <>
                              {/* Left arrow */}
                              <button
                                onClick={() => setCurrentPhotoIndex(prev => prev === 0 ? allPhotos.length - 1 : prev - 1)}
                                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                                aria-label="Previous photo"
                              >
                                <ChevronLeft className="w-6 h-6" />
                              </button>
                              
                              {/* Right arrow */}
                              <button
                                onClick={() => setCurrentPhotoIndex(prev => prev === allPhotos.length - 1 ? 0 : prev + 1)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                                aria-label="Next photo"
                              >
                                <ChevronRight className="w-6 h-6" />
                              </button>
                              
                              {/* Photo counter */}
                              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                                {currentPhotoIndex + 1} / {allPhotos.length}
                              </div>
                            </>
                          )}
                        </>
                      );
                    })()}
                    
                    {caseData.is_urgent && (
                      <Badge className="absolute top-4 left-4 bg-red-500 text-white border-0 urgent-badge">
                        <AlertTriangle className="w-4 h-4 mr-1" />
                        URGENT
                      </Badge>
                    )}
                    {isExternalCase && !caseData?.is_canonical && (
                      <Badge className={`absolute top-4 right-4 text-white border-0 ${
                        caseData.source === 'fbi'
                          ? 'bg-red-600'
                          : caseData.source === 'namus' 
                            ? 'bg-green-600' 
                            : 'bg-trust-blue-500'
                      }`}>
                        <Database className="w-4 h-4 mr-1" />
                        {caseData.source_name || caseData.source?.toUpperCase() || 'EXTERNAL'}
                      </Badge>
                    )}
                    {/* Merged/Canonical case badge - takes precedence over external badge */}
                    {caseData?.is_canonical && caseData?.sources?.length > 1 && (
                      <Badge className="absolute top-4 right-4 bg-trust-blue-500 text-white border-0">
                        <Database className="w-4 h-4 mr-1" />
                        Merged ({caseData.sources.length} sources)
                      </Badge>
                    )}
                    {caseData.status === 'found' && (
                      <div className="absolute inset-0 bg-green-500/80 flex items-center justify-center">
                        <span className="text-white text-3xl font-bold">FOUND</span>
                      </div>
                    )}
                    
                    {/* Desktop: Find Similar Faces overlay on image - hidden on mobile */}
                    {(() => {
                      // Feature flag: hide entirely unless explicitly enabled.
                      const facialEnabled = (process.env.REACT_APP_FACIAL_SEARCH_ENABLED === 'true');
                      if (!facialEnabled) return null;
                      const hasEncoding = Boolean(
                        caseData.face_encoding
                        || caseData.has_face_encoding
                        || caseData.facial_embedding_available,
                      );
                      const btn = (
                        <Button
                          size="sm"
                          disabled={!hasEncoding}
                          title={hasEncoding ? undefined : 'No facial encoding available for this case.'}
                          className={
                            hasEncoding
                              ? 'bg-trust-blue-600 hover:bg-trust-blue-700 text-white gap-1.5'
                              : 'bg-slate-400 text-white/80 gap-1.5 cursor-not-allowed opacity-60 hover:bg-slate-400'
                          }
                          data-testid="find-similar-faces-btn"
                        >
                          <Camera className="w-4 h-4" />
                          Find Similar Faces
                        </Button>
                      );
                      return (
                        <div className="hidden md:block absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 pt-12">
                          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
                            <div className="text-white text-sm">
                              <span className="opacity-80">See visually similar faces</span>
                            </div>
                            <div className="flex gap-2">
                              {hasEncoding ? (
                                <Link
                                  to={`/facial-search?caseKey=${encodeURIComponent(`${caseData.source || 'canonical'}:${caseData.external_id || caseData.id || id}`)}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {btn}
                                </Link>
                              ) : btn}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Info */}
                  <div className="p-6 lg:p-8">
                    {/* Mobile: Find Similar Faces - Non-overlapping, placed at top of info section */}
                    {(() => {
                      const facialEnabled = (process.env.REACT_APP_FACIAL_SEARCH_ENABLED === 'true');
                      if (!facialEnabled) return null;
                      const hasEncoding = Boolean(
                        caseData.face_encoding
                        || caseData.has_face_encoding
                        || caseData.facial_embedding_available,
                      );
                      const btn = (
                        <Button
                          size="sm"
                          disabled={!hasEncoding}
                          title={hasEncoding ? undefined : 'No facial encoding available for this case.'}
                          className={
                            hasEncoding
                              ? 'bg-trust-blue-600 hover:bg-trust-blue-700 text-white text-xs h-8'
                              : 'bg-slate-400 text-white/80 text-xs h-8 cursor-not-allowed opacity-60 hover:bg-slate-400'
                          }
                          data-testid="find-similar-faces-btn-mobile"
                        >
                          Find Similar Faces
                        </Button>
                      );
                      return (
                        <div className="mb-4 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700 md:hidden">
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-sm text-slate-600 dark:text-slate-400">
                              <Camera className="w-4 h-4 inline mr-1.5" />
                              See visually similar faces
                            </div>
                            {hasEncoding ? (
                              <Link to={`/facial-search?caseKey=${encodeURIComponent(`${caseData.source || 'canonical'}:${caseData.external_id || caseData.id || id}`)}`}>
                                {btn}
                              </Link>
                            ) : btn}
                          </div>
                        </div>
                      );
                    })()}
                    
                    <div className="flex flex-col gap-3 mb-4">
                      {/* Top row: Name + Status Badge */}
                      <div className="flex flex-col sm:flex-row sm:flex-wrap items-start gap-2">
                        <h1 className="font-heading text-2xl lg:text-3xl font-bold flex-1 min-w-0 break-words">
                          {formatDisplayName(caseData)}
                        </h1>
                        <Badge
                          variant={caseData.status === 'active' ? 'default' : 'secondary'}
                          className="flex-shrink-0 mt-1 sm:mt-0"
                        >
                          {caseData.status?.toUpperCase() || 'ACTIVE'}
                        </Badge>
                        {Array.isArray(caseData.flags)
                          && caseData.flags.includes('PODCAST_COVERAGE_AVAILABLE')
                          && (() => {
                            // Count distinct podcasts (dedupe multi-part series
                            // from the same show by `podcast_key`). Matches the
                            // logic used on MissingPersonCard search results.
                            const eps = Array.isArray(caseData.podcast_coverage_episodes)
                              ? caseData.podcast_coverage_episodes
                              : [];
                            const distinct = new Set(
                              eps.map((e) => e?.podcast_key).filter(Boolean)
                            ).size;
                            const count = distinct || 1;
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  const el = document.getElementById('podcast-coverage-panel');
                                  if (el) {
                                    el.scrollIntoView({
                                      behavior: 'smooth',
                                      block: 'start',
                                    });
                                  }
                                }}
                                data-testid="case-detail-podcast-badge"
                                title={`Covered in ${count} podcast${count === 1 ? '' : 's'} — click to view`}
                                className="flex-shrink-0 mt-1 sm:mt-0 inline-flex items-center gap-1
                                           rounded-full border border-purple-300 bg-purple-100
                                           px-2.5 py-0.5 text-xs font-semibold text-purple-800
                                           hover:bg-purple-200 hover:border-purple-400
                                           focus:outline-none focus-visible:ring-2
                                           focus-visible:ring-purple-500 transition-colors
                                           cursor-pointer">
                                <Mic className="w-3 h-3" />
                                <span>
                                  Covered in {count} podcast{count === 1 ? '' : 's'}
                                </span>
                              </button>
                            );
                          })()}
                      </div>

                      {/* Curator-verified enrichment flags (e.g.
                          ACTIVE_HOMICIDE_INVESTIGATION,
                          WEB_ENRICHED_2026). Rendered next to the
                          status badges so a viewer sees the critical
                          context at-a-glance. Admin gets a small
                          pencil button to open the flag editor and
                          add/remove flags directly. */}
                      <div className="flex items-center flex-wrap gap-1.5">
                        <EnrichmentFlagBadges flags={caseData?.flags} />
                        {user?.is_admin && (
                          <CaseFlagEditor
                            caseId={caseData.id || id}
                            flags={caseData?.flags || []}
                            onFlagsChange={(newFlags) =>
                              setCaseData((prev) => prev
                                ? { ...prev, flags: newFlags }
                                : prev)
                            }
                            trigger="pencil"
                          />
                        )}
                      </div>

                      {/* Also known as — surfaced when manual merge
                          combined multiple name spellings (married /
                          maiden / scraper-variant). Backed by
                          canonical.aliases. 2026-06-01. */}
                      {Array.isArray(caseData.aliases) && caseData.aliases.length > 0 && (
                        <div
                          data-testid="case-detail-aka-block"
                          className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm text-gray-600 dark:text-gray-400"
                        >
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            Also known as:
                          </span>
                          {caseData.aliases.map((alias, idx) => (
                            <span
                              key={`alias-${idx}`}
                              className="inline-flex items-center"
                            >
                              <span className="italic">{alias}</span>
                              {idx < caseData.aliases.length - 1 && (
                                <span aria-hidden="true" className="mx-1.5 text-gray-400">·</span>
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Admin buttons row (only shown to admins) */}
                      {user?.is_admin && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link to={`/admin/edit-case/${caseData.id || id}`}>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-blue-600 border-blue-300 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-700 dark:hover:bg-blue-950/50"
                              data-testid="admin-edit-case-btn"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                          </Link>
                          <Button 
                            variant={caseData.is_featured ? "default" : "outline"}
                            size="sm"
                            className={caseData.is_featured 
                              ? "bg-amber-500 hover:bg-amber-600 text-white" 
                              : "text-amber-600 border-amber-300 hover:bg-amber-50 dark:text-amber-400 dark:border-amber-700"
                            }
                            onClick={async () => {
                              try {
                                await updateCaseFeaturedStatus(caseData.id, !caseData.is_featured);
                                toast.success(caseData.is_featured ? 'Removed from featured' : 'Added to featured');
                                window.location.reload();
                              } catch (err) {
                                toast.error('Failed to update featured status');
                              }
                            }}
                            data-testid="admin-featured-toggle"
                          >
                            <Star className={`w-4 h-4 mr-1 ${caseData.is_featured ? 'fill-white' : ''}`} />
                            {caseData.is_featured ? 'Featured' : 'Feature'}
                          </Button>
                          <CreateOutreachButton caseData={caseData} />
                          {!caseData?.is_identified && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 dark:text-emerald-400 dark:border-emerald-700 dark:hover:bg-emerald-950/50"
                              onClick={() => setMarkIdentifiedOpen(true)}
                              data-testid="admin-mark-identified-btn"
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Mark identified
                            </Button>
                          )}
                          {caseData?.is_identified && (
                            <span
                              className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 px-2.5 py-1 text-xs font-semibold dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800"
                              data-testid="case-identified-badge"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              Identified
                              {caseData.identified_name && (
                                <span className="font-normal">
                                  — {caseData.identified_name}
                                </span>
                              )}
                            </span>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-amber-700 border-amber-300 hover:bg-amber-50 dark:text-amber-400 dark:border-amber-700 dark:hover:bg-amber-950/50"
                            onClick={() => setMoExclusionOpen(true)}
                            data-testid="mo-exclusion-cascade-button"
                          >
                            <ShieldAlert className="w-4 h-4 mr-1" />
                            MO Exclusion
                          </Button>
                        </div>
                      )}
                      <MarkIdentifiedDialog
                        caseId={caseData?.id || id}
                        caseName={caseData?.name || caseData?.case_number || id}
                        open={markIdentifiedOpen}
                        onOpenChange={setMarkIdentifiedOpen}
                        onResolved={() => {
                          setTimeout(() => window.location.reload(), 1500);
                        }}
                      />
                      <MOExclusionDialog
                        caseId={caseData?.id || id}
                        caseLabel={caseData?.name || caseData?.case_number || caseData?.namus_number || (caseData?.id || id)}
                        open={moExclusionOpen}
                        onOpenChange={setMoExclusionOpen}
                      />
                      
                      {/* Demographics row */}
                      <div className="flex items-center gap-2 text-muted-foreground flex-wrap">
                        {(caseData.age || caseData.age_min || caseData.age_max || caseData.age_range) && (
                          <span>{formatAge(caseData)}</span>
                        )}
                        {(caseData.age || caseData.age_min || caseData.age_max || caseData.age_range) && caseData.gender && <span>•</span>}
                        {caseData.gender && <span>{caseData.gender}</span>}
                        {caseData.date_of_birth && (
                          <>
                            <span>•</span>
                            <span>DOB: {caseData.date_of_birth}</span>
                          </>
                        )}
                      </div>
                    </div>

                    <Separator className="my-4" />

                    <div className="space-y-4">
                      {/* Location - contextual label based on case type */}
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-safety-orange-500 mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {(caseData.source === 'namus_unidentified' || 
                              caseData.source === 'namus_unclaimed' || 
                              caseData.source === 'doenetwork' ||
                              caseData.case_type === 'unclaimed_person' || 
                              caseData.case_type === 'unidentified_person' ||
                              caseData.case_type === 'unidentified') 
                              ? 'Recovery Location' 
                              : 'Last Seen Location'}
                          </p>
                          <p className="font-medium">{getLocation()}</p>
                        </div>
                      </div>

                      {/* Date - contextual label based on case type */}
                      <div className="flex items-start gap-3">
                        <Calendar className="w-5 h-5 text-trust-blue-500 mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {(() => {
                              const isUnidentified = (
                                caseData.source === 'namus_unidentified' || 
                                caseData.source === 'namus_unclaimed' || 
                                caseData.source === 'doenetwork' ||
                                caseData.case_type === 'unclaimed_person' || 
                                caseData.case_type === 'unidentified_person' ||
                                caseData.case_type === 'unidentified'
                              );
                              
                              if (!isUnidentified) return 'Last Seen Date';
                              
                              // For unidentified remains
                              if (caseData.source === 'namus_unclaimed' || caseData.case_type === 'unclaimed_person') {
                                return 'Date Body Found';
                              }
                              
                              // Check if we have actual date found vs estimated year
                              if (caseData.date_found || caseData.date_body_found) {
                                return 'Date Found';
                              }
                              if (caseData.estimated_year_of_death) {
                                return 'Est. Year of Death';
                              }
                              return 'Date Found';
                            })()}
                          </p>
                          <p className="font-medium">
                            {(() => {
                              const isUnidentified = (
                                caseData.source === 'namus_unidentified' || 
                                caseData.source === 'namus_unclaimed' || 
                                caseData.source === 'doenetwork' ||
                                caseData.case_type === 'unclaimed_person' || 
                                caseData.case_type === 'unidentified_person' ||
                                caseData.case_type === 'unidentified'
                              );
                              
                              if (isUnidentified) {
                                const dateVal = caseData.date_body_found || caseData.date_found;
                                if (dateVal) return formatDate(dateVal);
                                if (caseData.estimated_year_of_death) return caseData.estimated_year_of_death;
                                return '—';
                              }
                              return formatDate(caseData.last_seen_date || caseData.missing_since, { fallback: '—' });
                            })()}
                          </p>
                        </div>
                      </div>

                      {/* DOB and Age for unclaimed cases */}
                      {(caseData.source === 'namus_unclaimed' || caseData.case_type === 'unclaimed_person') && (
                        <>
                          {(caseData.date_of_birth || caseData.year_of_birth) && (
                            <div className="flex items-start gap-3">
                              <Calendar className="w-5 h-5 text-purple-500 mt-0.5" />
                              <div>
                                <p className="text-sm text-muted-foreground">Date of Birth</p>
                                <p className="font-medium">
                                  {caseData.date_of_birth 
                                    ? formatDate(caseData.date_of_birth)
                                    : caseData.year_of_birth 
                                      ? `Year: ${caseData.year_of_birth}`
                                      : 'Unknown'}
                                </p>
                              </div>
                            </div>
                          )}
                          {(caseData.age || caseData.age_min || caseData.age_max || caseData.age_range) && (
                            <div className="flex items-start gap-3">
                              <User className="w-5 h-5 text-indigo-500 mt-0.5" />
                              <div>
                                <p className="text-sm text-muted-foreground">Estimated Age at Death</p>
                                <p className="font-medium">{formatAge(caseData)}</p>
                              </div>
                            </div>
                          )}
                          {caseData.date_of_death && (
                            <div className="flex items-start gap-3">
                              <Calendar className="w-5 h-5 text-slate-500 mt-0.5" />
                              <div>
                                <p className="text-sm text-muted-foreground">Date of Death</p>
                                <p className="font-medium">{formatDate(caseData.date_of_death)}</p>
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {caseData.contact_info && (
                        <div className="flex items-start gap-3">
                          <Phone className="w-5 h-5 text-green-500 mt-0.5" />
                          <div>
                            <p className="text-sm text-muted-foreground">Contact Information</p>
                            <p className="font-medium">{caseData.contact_info}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Investigation Factors panel moved to a
                        standalone card above the matches section so
                        the radius/factor decisions are visible BEFORE
                        the investigator scans match results. */}

                    <div className="flex flex-wrap gap-3 mt-6">
                      {isExternalCase ? (
                        <>
                          <a href={caseData.source_url} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-[200px]">
                            <Button className={`w-full text-white rounded-full ${
                              caseData.source === 'fbi'
                                ? 'bg-red-600 hover:bg-red-700'
                                : caseData.source === 'namus' 
                                  ? 'bg-green-600 hover:bg-green-700' 
                                  : 'bg-trust-blue-500 hover:bg-trust-blue-600'
                            }`}>
                              <ExternalLink className="w-4 h-4 mr-2" />
                              View on {caseData.source_name || caseData.source?.toUpperCase() || 'Source'}
                            </Button>
                          </a>
                          
                          {/* View on Merged - For canonical/merged cases */}
                          {caseData?.is_canonical && caseData?.sources?.length > 1 && (
                            <Button
                              onClick={scrollToMergedSources}
                              className="bg-trust-blue-500 hover:bg-trust-blue-600 text-white rounded-full whitespace-nowrap"
                              data-testid="view-merged-sources-btn"
                            >
                              <Database className="w-4 h-4 mr-2" />
                              View on Merged ({caseData.sources.length} sources)
                            </Button>
                          )}
                          
                          <Button 
                            variant="outline" 
                            onClick={addCurrentToCompare}
                            className="rounded-full whitespace-nowrap"
                          >
                            <Scale className="w-4 h-4 mr-2" />
                            Compare
                          </Button>
                          <Button
                            onClick={() => setShowFlyerModal(true)}
                            variant="outline"
                            className="rounded-full gap-2 whitespace-nowrap"
                            data-testid="print-flyer-btn"
                          >
                            <Printer className="w-4 h-4" />
                            Print Flyer
                          </Button>
                          
                          {/* Save Case Button - Mobile */}
                          <SaveCaseButton
                            caseId={caseData.id}
                            caseData={{
                              case_type: caseData.case_type || 'external',
                              name: caseData.name,
                              thumbnail_url: caseData.photo_url
                            }}
                            variant="outline"
                            className="rounded-full whitespace-nowrap"
                          />
                          
                          {/* Boost to Featured Button */}
                          {user && featuredCredits?.can_feature && !caseData.is_featured && (
                            <Button
                              onClick={handleBoostCase}
                              disabled={boostLoading || featuredCredits.available_credits <= 0}
                              className="rounded-full gap-2 bg-amber-500 hover:bg-amber-600 text-white whitespace-nowrap"
                            >
                              {boostLoading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Star className="w-4 h-4" />
                              )}
                              Boost to Featured
                            </Button>
                          )}
                          
                          {/* Already Featured Badge */}
                          {caseData.is_featured && (
                            <Badge className="bg-amber-100 text-amber-700 border border-amber-300 rounded-full px-3 py-1">
                              <Star className="w-3 h-3 mr-1 fill-amber-500" />
                              Featured Case
                            </Badge>
                          )}
                        </>
                      ) : (
                        <>
                          <Link to={`/tip/${caseData.id}`} className="flex-1">
                            <Button className="w-full bg-safety-orange-500 hover:bg-safety-orange-600 text-white rounded-full" data-testid="submit-tip-btn">
                              <MessageCircle className="w-4 h-4 mr-2" />
                              Submit Tip
                            </Button>
                          </Link>
                          
                          {/* View on Merged - For canonical/merged cases */}
                          {caseData?.is_canonical && caseData?.sources?.length > 1 && (
                            <Button
                              onClick={scrollToMergedSources}
                              className="bg-trust-blue-500 hover:bg-trust-blue-600 text-white rounded-full flex-1 min-w-[200px]"
                              data-testid="view-merged-sources-btn"
                            >
                              <Database className="w-4 h-4 mr-2" />
                              View on Merged ({caseData.sources.length} sources)
                            </Button>
                          )}
                          
                          <ShareFlyer caseData={caseData} />
                          <Button
                            onClick={() => setShowFlyerModal(true)}
                            variant="outline"
                            className="rounded-full gap-2"
                            data-testid="print-flyer-btn"
                          >
                            <Printer className="w-4 h-4" />
                            Print Flyer
                          </Button>
                          
                          {/* Save Case Button */}
                          <SaveCaseButton
                            caseId={caseData.id}
                            caseData={{
                              case_type: caseData.case_type || 'external',
                              name: caseData.name,
                              thumbnail_url: caseData.photo_url
                            }}
                            variant="outline"
                            className="rounded-full"
                          />
                          
                          {/* Boost to Featured Button */}
                          {user && featuredCredits?.can_feature && !caseData.is_featured && (
                            <Button
                              onClick={handleBoostCase}
                              disabled={boostLoading || featuredCredits.available_credits <= 0}
                              className="rounded-full gap-2 bg-amber-500 hover:bg-amber-600 text-white"
                            >
                              {boostLoading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Star className="w-4 h-4" />
                              )}
                              Boost to Featured
                            </Button>
                          )}
                          
                          {/* Already Featured Badge */}
                          {caseData.is_featured && (
                            <Badge className="bg-amber-100 text-amber-700 border border-amber-300 rounded-full px-3 py-1">
                              <Star className="w-3 h-3 mr-1 fill-amber-500" />
                              Featured Case
                            </Badge>
                          )}
                        </>
                      )}
                    </div>

                    {/* Share Section */}
                    <div className="mt-6 pt-4 border-t">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Share2 className="w-4 h-4" />
                          <span>Share this case</span>
                          {shareCount > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {shareCount} {shareCount === 1 ? 'share' : 'shares'}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <ShareButtons 
                        caseData={caseData} 
                        caseId={id}
                        showLabel={false}
                        onShareComplete={(newCount) => setShareCount(newCount)}
                      />

                      {/* One-click branded share bar — uses the short
                          /c/{id} URL with SSR meta tags so Twitter /
                          Slack / iMessage previews render the per-case
                          OG image even without JS execution. */}
                      <div className="mt-3">
                        <CaseShareBar caseData={caseData} caseId={id} />
                      </div>
                    </div>
                    
                    {/* Admin: Photo Management */}
                    {(user?.role === 'admin' || user?.role === 'superadmin') && (
                      <div className="mt-4 pt-4 border-t">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                          <Camera className="w-4 h-4" />
                          <span>Admin Actions</span>
                        </div>
                        <PhotoManager 
                          caseId={id} 
                          isAdmin={true}
                          onPhotosUpdated={() => {
                            // Re-fetch case data (NOT window.location.reload).
                            // A hard reload races the upload's read-after-
                            // write and also aborts any pending fetches —
                            // that's why uploads previously appeared to
                            // require a second click before the new photo
                            // appeared. fetchCaseData() lets React do the
                            // refresh cleanly and re-renders <img> with the
                            // new base64 data URI, which has its own unique
                            // hash so the browser image cache can't serve
                            // the stale one.
                            fetchCaseData();
                          }}
                        />
                      </div>
                    )}

                    {/* Admin: Outreach letter history for this case */}
                    {(user?.role === 'admin' || user?.role === 'superadmin' ||
                      user?.is_admin) && (
                      <div className="mt-4 pt-4 border-t">
                        <CaseOutreachHistory caseId={id} />
                      </div>
                    )}

                    {/* Third-party podcast coverage — public (any viewer) */}
                    <div id="podcast-coverage-panel" className="mt-4 scroll-mt-24">
                      <CasePodcastCoverage
                        caseData={caseData}
                        isAdmin={!!user?.is_admin}
                        onFlagCleared={() => {
                          // Re-fetch case data so the NEEDS_REVIEW badge and
                          // clear button disappear without a hard reload.
                          fetchCaseData();
                        }}
                      />
                    </div>

                    {/* Case References — compact panel showing every
                        external identifier (NamUs #, Doe Network ID,
                        NCIC, agency case #, ME case #, DDP URL, source
                        URL, and copy-share-link button). Only renders
                        when at least one identifier is populated. */}
                    <CaseReferences caseData={caseData} />

                    {/* Investigator notes — curator-verified case
                        context. Read-only for regular logged-in users,
                        editable for admins. Renders below podcast
                        coverage so the visual ordering mirrors the
                        information hierarchy (matches → coverage →
                        enrichment notes). */}
                    <div id="investigator-notes-panel" className="scroll-mt-24">
                      <CaseInvestigatorNotes
                        caseId={caseData?.id}
                        notes={caseData?.investigator_notes || []}
                        isAdmin={!!user?.is_admin}
                        onNoteAdded={() => fetchCaseData()}
                      />
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Conversion CTA — only for logged-out visitors. Reddit/social
                traffic lands on case pages with no offer; this surfaces
                the trial. Captures `?ref=` via existing referral capture
                in App.js so signups attribute back to source. */}
            {!user && (
              <SignupCTA variant="case" context={`case:${id}`} ctaTestId="case-detail-signup-cta" />
            )}

            {/* Additional Photos Gallery */}
            {((caseData.additional_photos && caseData.additional_photos.length > 0) || 
              (caseData.all_photo_urls && caseData.all_photo_urls.length > 1) ||
              (caseData.photos && caseData.photos.length > 1)) && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <ImageIcon className="w-5 h-5 text-trust-blue-500" />
                    Additional Photos ({(caseData.additional_photos?.length || (caseData.all_photo_urls?.length - 1) || (caseData.photos?.length - 1) || 0) + 1} total)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {/* Main photo thumbnail */}
                    <div className="relative aspect-square rounded-lg overflow-hidden border-2 border-trust-blue-500">
                      <NamusImage
                        src={getImage()}
                        alt={`${caseData.name} - Main`}
                        className="w-full h-full object-cover"
                        fallbackSrc={placeholderImages[0]}
                      />
                      <Badge className="absolute bottom-1 left-1 bg-trust-blue-500 text-white text-xs">Main</Badge>
                    </div>
                    {/* all_photo_urls (from NamUs unidentified/unclaimed) - skip first as it's the main */}
                    {caseData.all_photo_urls && caseData.all_photo_urls.slice(1).map((photoUrl, idx) => {
                      const secureUrl = photoUrl?.replace('http://', 'https://');
                      return (
                        <div
                          key={`allphoto-${idx}`}
                          className="relative aspect-square rounded-lg overflow-hidden border hover:border-trust-blue-400 transition-colors group cursor-pointer"
                          onClick={() => openPhoto(secureUrl)}
                        >
                          <NamusImage
                            src={secureUrl}
                            alt={`${caseData.name} - Photo ${idx + 2}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                            <ExternalLink className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      );
                    })}
                    {/* photos[] flat-string array (TXDPS, CA DOJ, CO CBI, VA VSP) — skip
                        first as it's the main. 2026-06-01 fix: 1,897 records across
                        10 sources stored extra photos here and the gallery silently
                        ignored them. Suppressed when `all_photo_urls` already covers
                        the case so the same image set doesn't render twice. */}
                    {(!caseData.all_photo_urls || caseData.all_photo_urls.length <= 1) &&
                     caseData.photos && caseData.photos.slice(1).map((photoUrl, idx) => {
                      const secureUrl = photoUrl?.replace('http://', 'https://');
                      return (
                        <div
                          key={`photos-${idx}`}
                          className="relative aspect-square rounded-lg overflow-hidden border hover:border-trust-blue-400 transition-colors group cursor-pointer"
                          onClick={() => openPhoto(secureUrl)}
                        >
                          <NamusImage
                            src={secureUrl}
                            alt={`${caseData.name} - Photo ${idx + 2}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                            <ExternalLink className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      );
                    })}
                    {/* additional_photos (from Charley/other sources) */}
                    {caseData.additional_photos && caseData.additional_photos.map((photo, idx) => {
                      // Handle both string URLs (Charley) and object format (NamUs)
                      const photoUrl = typeof photo === 'string' ? photo : (photo.url || photo.thumbnail);
                      const thumbnailUrl = typeof photo === 'string' ? photo : (photo.thumbnail || photo.url);
                      const caption = typeof photo === 'string' ? null : photo.caption;
                      const category = typeof photo === 'string' ? null : photo.category;
                      // Convert http to https for Charley Project URLs
                      const securePhotoUrl = photoUrl?.replace('http://', 'https://');
                      const secureThumbnailUrl = thumbnailUrl?.replace('http://', 'https://');
                      
                      return (
                        <div
                          key={idx}
                          className="relative aspect-square rounded-lg overflow-hidden border hover:border-trust-blue-400 transition-colors group cursor-pointer"
                          onClick={() => openPhoto(securePhotoUrl)}
                        >
                          <NamusImage
                            src={secureThumbnailUrl}
                            alt={caption || `${caseData.name} - Photo ${idx + 2}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                          {caption && (
                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1 truncate">
                              {caption}
                            </div>
                          )}
                          {category && (
                            <Badge className="absolute top-1 right-1 bg-purple-500/80 text-white text-[10px] px-1 py-0">
                              {category}
                            </Badge>
                          )}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                            <ExternalLink className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Related Cases Section - People who went missing together */}
            {relatedCases.length > 0 && (
              <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading flex items-center gap-2 text-amber-800 dark:text-amber-200">
                    <Link2 className="w-5 h-5" />
                    Related Cases ({relatedCases.length})
                  </CardTitle>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    {relatedCases[0]?.relationship_description || 'Connected cases'}
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {relatedCases.map((relatedCase) => (
                      <Link
                        key={relatedCase.id}
                        to={`/case/${relatedCase.id}`}
                        className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-slate-800 border border-amber-200 dark:border-amber-700 hover:border-amber-400 dark:hover:border-amber-500 transition-colors group"
                      >
                        <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 border">
                          <NamusImage
                            src={relatedCase.photo_url || relatedCase.photo_thumbnail}
                            alt={relatedCase.name}
                            className="w-full h-full object-cover"
                            fallbackSrc="/placeholder-person.png"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm truncate group-hover:text-amber-700 dark:group-hover:text-amber-300 transition-colors">
                            {relatedCase.name}
                          </h4>
                          <p className="text-xs text-muted-foreground">
                            {relatedCase.age && `Age ${relatedCase.age}`}
                            {relatedCase.age && relatedCase.gender && ' • '}
                            {relatedCase.gender}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {relatedCase.state}
                            {relatedCase.last_seen_date && ` • ${new Date(relatedCase.last_seen_date).getFullYear()}`}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-amber-600 transition-colors" />
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Investigation Factors (Admin Only) — placed above the
                matches section so the search-radius / factor decisions
                are visible before match results are reviewed. Render
                only for admins to avoid bloating the page for the
                public case-view audience. */}
            {user?.is_admin && (
              <InvestigationFlags
                caseId={caseData.id || id}
                caseType={
                  isExternalCase
                    ? 'external'
                    : (caseData.case_type === 'unidentified_person' ||
                       caseData.source === 'namus_unidentified' ||
                       caseData.source === 'namus_unclaimed')
                      ? 'unidentified'
                      : 'missing'
                }
                isAdmin={user?.is_admin}
              />
            )}

            {/* Case Information Section - Structured hierarchy with source attribution */}
            <CaseDescriptionSection caseData={caseData} />

            {/* Physical Details Card */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <User className="w-5 h-5 text-slate-500" />
                  Physical Details & Evidence
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Physical Characteristics Grid */}
                {(caseData.hair_color || caseData.eye_color || caseData.height || caseData.weight || caseData.race || caseData.date_of_birth || caseData.place_of_birth || caseData.date_found || caseData.date_body_found) && (
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Physical Characteristics
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {/* Date Found - Important for unidentified/unclaimed */}
                      {(caseData.date_found || caseData.date_body_found) && (
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide">
                            {caseData.source === 'namus_unclaimed' ? 'Body Found' : 'Date Found'}
                          </span>
                          <span className="font-medium">
                            {formatDate(caseData.date_found || caseData.date_body_found)}
                          </span>
                        </div>
                      )}
                      {/* Estimated Year of Death - for unidentified */}
                      {caseData.estimated_year_of_death && (
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide">Est. Year of Death</span>
                          <span className="font-medium">{caseData.estimated_year_of_death}</span>
                        </div>
                      )}
                      {caseData.date_of_birth && (
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide">Date of Birth</span>
                          <span className="font-medium">{caseData.date_of_birth}</span>
                        </div>
                      )}
                      {caseData.place_of_birth && (
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide">Place of Birth</span>
                          <span className="font-medium">{caseData.place_of_birth}</span>
                        </div>
                      )}
                      {/* Age - especially important for unidentified */}
                      {(caseData.age || caseData.age_min || caseData.age_max || caseData.age_range) && (
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide">
                            {caseData.source?.includes('unidentified') || caseData.source?.includes('unclaimed') ? 'Est. Age' : 'Age'}
                          </span>
                          <span className="font-medium">{formatAge(caseData, { compact: true, showEstimated: false })}</span>
                        </div>
                      )}
                      {caseData.hair_color && caseData.hair_color !== 'Unknown' && (
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide">Hair</span>
                          <span className="font-medium">{caseData.hair_color}</span>
                        </div>
                      )}
                      {caseData.eye_color && caseData.eye_color !== 'Unknown' && (
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide">Eyes</span>
                          <span className="font-medium">{caseData.eye_color}</span>
                        </div>
                      )}
                      {caseData.height && (
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide">Height</span>
                          <span className="font-medium">{caseData.height}</span>
                        </div>
                      )}
                      {caseData.height_display && !caseData.height && (
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide">Height</span>
                          <span className="font-medium">{caseData.height_display}</span>
                        </div>
                      )}
                      {caseData.weight && (
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide">Weight</span>
                          <span className="font-medium">{caseData.weight}</span>
                        </div>
                      )}
                      {caseData.weight_lbs && !caseData.weight && (
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide">Weight</span>
                          <span className="font-medium">{caseData.weight_lbs} lbs</span>
                        </div>
                      )}
                      {caseData.race && (
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide">Race/Ethnicity</span>
                          <span className="font-medium">{caseData.race}</span>
                        </div>
                      )}
                      {caseData.nationality && (
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide">Nationality</span>
                          <span className="font-medium">{caseData.nationality}</span>
                        </div>
                      )}
                      {caseData.occupations && caseData.occupations.length > 0 && (
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide">Occupation</span>
                          <span className="font-medium">{caseData.occupations.join(', ')}</span>
                        </div>
                      )}
                      {caseData.languages && caseData.languages.length > 0 && (
                        <div className="flex flex-col col-span-2">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide">Languages</span>
                          <span className="font-medium">{caseData.languages.join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Remains Information - For Unidentified Persons */}
                {(caseData.case_type === 'unidentified_person' || caseData.source === 'namus_unidentified' || caseData.source === 'doenetwork') && 
                 (caseData.condition_of_remains || caseData.inventory_status || caseData.time_since_death_display || caseData.estimated_year_of_death || caseData.estimated_death_date || caseData.state_of_remains || caseData.cause_of_death) && (
                  <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4" data-testid="remains-info-section">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Database className="w-4 h-4" />
                      Remains Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Condition of Remains / State of Remains */}
                      {(caseData.condition_of_remains || caseData.state_of_remains) && (
                        <div className="flex flex-col p-3 bg-white dark:bg-slate-900 rounded-lg border">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Condition of Remains</span>
                          <span className="font-medium text-sm">{caseData.condition_of_remains || caseData.state_of_remains}</span>
                        </div>
                      )}
                      
                      {/* Cause of Death */}
                      {caseData.cause_of_death && (
                        <div className="flex flex-col p-3 bg-white dark:bg-slate-900 rounded-lg border">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Cause of Death</span>
                          <span className="font-medium text-sm">{caseData.cause_of_death}</span>
                        </div>
                      )}
                      
                      {/* Inventory of Remains */}
                      {caseData.inventory_status && (
                        <div className="flex flex-col p-3 bg-white dark:bg-slate-900 rounded-lg border">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Body Recovery Status</span>
                          <span className={`font-medium text-sm ${
                            caseData.inventory_status === 'All parts recovered' 
                              ? 'text-green-600 dark:text-green-400' 
                              : caseData.inventory_status === 'Partial remains' 
                                ? 'text-amber-600 dark:text-amber-400' 
                                : ''
                          }`}>
                            {caseData.inventory_status}
                          </span>
                          {caseData.inventory_description && caseData.inventory_status !== 'All parts recovered' && (
                            <span className="text-xs text-muted-foreground mt-1">{caseData.inventory_description}</span>
                          )}
                        </div>
                      )}
                      
                      {/* Time Since Death (Estimated PMI in layman's terms) */}
                      {caseData.time_since_death_display && (
                        <div className="flex flex-col p-3 bg-white dark:bg-slate-900 rounded-lg border">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Estimated Time Since Death</span>
                          <span className="font-medium text-sm">{caseData.time_since_death_display}</span>
                          <span className="text-xs text-muted-foreground mt-1">When remains were discovered</span>
                        </div>
                      )}
                      
                      {/* Estimated Year of Death */}
                      {(caseData.estimated_year_of_death || caseData.estimated_death_date) && (
                        <div className="flex flex-col p-3 bg-white dark:bg-slate-900 rounded-lg border">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Estimated Date of Death</span>
                          <span className="font-medium text-sm">{caseData.estimated_year_of_death || caseData.estimated_death_date}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Consolidated Physical Features Section */}
                {/* Combines: distinguishing_features, scars_and_marks, physical_features array, scars array, tattoos array */}
                {(() => {
                  // Helper to clean encoding issues in text
                  const cleanText = (text) => {
                    if (!text) return text;
                    return text
                      .replace(/â€[œ""]/g, '"')  // Smart quotes
                      .replace(/â€™/g, "'")       // Smart apostrophe
                      .replace(/â€"/g, '-')       // En-dash
                      .replace(/â€"/g, '--')      // Em-dash
                      .replace(/â€¦/g, '...')     // Ellipsis
                      .replace(/â€\|/g, '"')      // Broken quote
                      .replace(/Ã©/g, 'é')        // Accented e
                      .replace(/Ã¡/g, 'á')        // Accented a
                      .replace(/â€œæ/g, '')       // Garbage characters
                      .replace(/[^\x20-\x7E\n\r]/g, '') // Remove other non-printable
                      .trim();
                  };
                  
                  // Collect all unique descriptions to avoid duplication
                  const seenDescriptions = new Set();
                  const allFeatures = [];
                  
                  // Normalize description for comparison (lowercase, remove extra spaces)
                  const normalizeForCompare = (text) => {
                    if (!text) return '';
                    return cleanText(text).toLowerCase().replace(/\s+/g, ' ').trim();
                  };
                  
                  // Add physical_features (structured data with type badges)
                  if (caseData.physical_features && Array.isArray(caseData.physical_features)) {
                    caseData.physical_features.forEach((feature, idx) => {
                      const desc = normalizeForCompare(feature.description);
                      if (desc && !seenDescriptions.has(desc)) {
                        seenDescriptions.add(desc);
                        allFeatures.push({ type: feature.type, description: cleanText(feature.description), key: `pf-${idx}` });
                      }
                    });
                  }
                  
                  // Add scars array (only if not already in physical_features)
                  if (caseData.scars && Array.isArray(caseData.scars)) {
                    caseData.scars.forEach((scar, idx) => {
                      const desc = normalizeForCompare(scar);
                      if (desc && !seenDescriptions.has(desc)) {
                        seenDescriptions.add(desc);
                        allFeatures.push({ type: 'Scar', description: cleanText(scar), key: `scar-${idx}` });
                      }
                    });
                  }
                  
                  // Add tattoos array (only if not already in physical_features)
                  if (caseData.tattoos && Array.isArray(caseData.tattoos)) {
                    caseData.tattoos.forEach((tattoo, idx) => {
                      const desc = normalizeForCompare(tattoo);
                      if (desc && !seenDescriptions.has(desc)) {
                        seenDescriptions.add(desc);
                        allFeatures.push({ type: 'Tattoo', description: cleanText(tattoo), key: `tattoo-${idx}` });
                      }
                    });
                  }
                  
                  // Parse scars_and_marks or distinguishing_features text for additional items
                  // Only if we don't already have structured data
                  if (allFeatures.length === 0) {
                    const rawText = caseData.scars_and_marks || caseData.distinguishing_features;
                    if (rawText) {
                      // Split by newlines or semicolons
                      const items = rawText.split(/[\n;]+/).map(s => cleanText(s)).filter(Boolean);
                      items.forEach((item, idx) => {
                        const desc = normalizeForCompare(item);
                        if (desc && !seenDescriptions.has(desc)) {
                          seenDescriptions.add(desc);
                          // Try to detect type
                          const lowerItem = item.toLowerCase();
                          let type = 'Feature';
                          if (lowerItem.includes('scar')) type = 'Scar';
                          else if (lowerItem.includes('tattoo')) type = 'Tattoo';
                          else if (lowerItem.includes('birthmark')) type = 'Birthmark';
                          else if (lowerItem.includes('piercing')) type = 'Piercing';
                          allFeatures.push({ type, description: item, key: `text-${idx}` });
                        }
                      });
                    }
                  }
                  
                  // Check if we have any features to display
                  if (allFeatures.length === 0) return null;
                  
                  return (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                      <h4 className="font-semibold mb-3 flex items-center gap-2 text-amber-700 dark:text-amber-400">
                        <AlertTriangle className="w-4 h-4" />
                        Distinguishing Features / Scars & Marks
                      </h4>
                      <div className="space-y-3">
                        {/* Render all unique features - stacked layout for better mobile readability */}
                        {allFeatures.map((feature) => {
                          // Shorten long type names for mobile
                          const shortType = feature.type
                            ?.replace('Other distinctive physical characteristic', 'Physical')
                            ?.replace('distinctive physical characteristic', 'Physical')
                            ?.replace('characteristic', '')
                            ?.trim();
                          
                          return (
                            <div key={feature.key} className="flex flex-col sm:flex-row sm:gap-2 sm:items-start">
                              <Badge 
                                variant="secondary" 
                                className={`shrink-0 text-xs w-fit mb-1 sm:mb-0 ${
                                  feature.type?.toLowerCase().includes('tattoo') 
                                    ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300' 
                                    : feature.type?.toLowerCase().includes('scar') 
                                      ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                                      : 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
                                }`}
                              >
                                {shortType || feature.type}
                              </Badge>
                              <span className="text-sm text-muted-foreground">{feature.description}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                
                {/* Remarks / Medical Information */}
                {caseData.remarks && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <h4 className="font-semibold mb-2 flex items-center gap-2 text-red-700 dark:text-red-400">
                      <AlertTriangle className="w-4 h-4" />
                      Important Medical/Additional Information
                    </h4>
                    <p className="text-muted-foreground whitespace-pre-line">
                      {caseData.remarks}
                    </p>
                  </div>
                )}
                
                {/* Hair Description (from NamUs enrichment) */}
                {(caseData.hair_description || caseData.facial_hair) && (() => {
                  // Helper to clean encoding issues
                  const cleanText = (text) => {
                    if (!text) return text;
                    return text
                      .replace(/â€[œ""]/g, '"')
                      .replace(/â€™/g, "'")
                      .replace(/â€"/g, '-')
                      .replace(/â€"/g, '--')
                      .replace(/â€¦/g, '...')
                      .replace(/â€\|/g, '"')
                      .replace(/â€œæ/g, '')
                      .replace(/[^\x20-\x7E\n\r]/g, '')
                      .trim();
                  };
                  
                  const hairDesc = cleanText(caseData.hair_description);
                  const facialHair = cleanText(caseData.facial_hair);
                  
                  if (!hairDesc && !facialHair) return null;
                  
                  return (
                    <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                      <h4 className="font-semibold mb-3 flex items-center gap-2 text-orange-700 dark:text-orange-400">
                        <User className="w-4 h-4" />
                        Hair Description
                      </h4>
                      {hairDesc && (
                        <p className="text-sm text-muted-foreground mb-2">
                          <span className="font-medium">Head Hair:</span> {hairDesc}
                        </p>
                      )}
                      {facialHair && (
                        <p className="text-sm text-muted-foreground">
                          <span className="font-medium">Facial Hair:</span> {facialHair}
                        </p>
                      )}
                    </div>
                  );
                })()}
                
                {/* Possible Name (from NamUs enrichment) */}
                {caseData.possible_first_name && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <h4 className="font-semibold mb-2 flex items-center gap-2 text-green-700 dark:text-green-400">
                      <User className="w-4 h-4" />
                      Possible Name
                    </h4>
                    <p className="text-muted-foreground">
                      This individual may have been known as <span className="font-semibold">&quot;{caseData.possible_first_name}&quot;</span>
                      {caseData.possible_last_name && <span className="font-semibold"> {caseData.possible_last_name}</span>}
                    </p>
                  </div>
                )}

                {/* Clothing & Accessories */}
                {(user?.is_admin || caseData.clothing_description || caseData.last_seen_clothing || (caseData.clothing_articles && caseData.clothing_articles.length > 0) || (Array.isArray(caseData.clothing) && caseData.clothing.length > 0) || (typeof caseData.clothing === 'string' && caseData.clothing) || (caseData.accessories && caseData.accessories.length > 0)) && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2 text-blue-700 dark:text-blue-400">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20.38 3.46L16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23z" />
                      </svg>
                      Last Known Clothing & Accessories
                    </h4>
                    {/* Admin manual-edits overlay panel: pencil icons
                        per row, "+ Add", soft-delete with reset, and
                        "edited" badges. Hidden for non-admins so the
                        read-only render below is what they see. */}
                    {user?.is_admin && (
                      <ManualEditPanel
                        caseId={caseData?.id}
                        sourceCase={caseData}
                        onChange={fetchCaseData}
                      />
                    )}
                    {/* Read-only render is suppressed for admins — they
                        manage the same data via ManualEditPanel above. */}
                    {!user?.is_admin && (
                    <div className="space-y-2">
                      {/* Render clothing from clothing_articles OR clothing array (not both to avoid duplicates) */}
                      {(() => {
                        // Helper to clean encoding issues
                        const cleanText = (text) => {
                          if (!text) return text;
                          return text
                            .replace(/â€[œ""]/g, '"')
                            .replace(/â€™/g, "'")
                            .replace(/â€"/g, '-')
                            .replace(/â€"/g, '--')
                            .replace(/â€¦/g, '...')
                            .replace(/â€\|/g, '"')
                            .replace(/â€œæ/g, '')
                            .replace(/[^\x20-\x7E\n\r,]/g, '')
                            .trim();
                        };
                        
                        // Normalize description for deduplication
                        // Removes type prefixes like "Underwear: " to catch duplicates
                        const normalizeForDedup = (desc) => {
                          if (!desc) return '';
                          let normalized = desc.toLowerCase().trim();
                          // Remove common type prefixes that might cause duplicates
                          const prefixes = ['underwear:', 'shirt:', 'pants:', 'shoes:', 'jacket:', 'socks:', 'hat:', 'belt:', 'jewelry:'];
                          for (const prefix of prefixes) {
                            if (normalized.startsWith(prefix)) {
                              normalized = normalized.slice(prefix.length).trim();
                              break;
                            }
                          }
                          return normalized;
                        };
                        
                        // Collect all items and deduplicate
                        const seenDescriptions = new Set();
                        const allItems = [];
                        
                        // First, add clothing_articles (structured data from NamUs)
                        if (caseData.clothing_articles?.length > 0) {
                          caseData.clothing_articles.forEach((item, idx) => {
                            const cleanedDesc = cleanText(item.description);
                            const descKey = normalizeForDedup(cleanedDesc);
                            if (descKey && !seenDescriptions.has(descKey)) {
                              seenDescriptions.add(descKey);
                              
                              // Extract type from description if it has "Type: description" format
                              let type = item.type || item.article?.name || 'Clothing';
                              let description = cleanedDesc;
                              
                              // If description starts with a type like "Underwear: ..." 
                              // extract it as the type for cleaner display
                              if (description && description.includes(':')) {
                                const colonIdx = description.indexOf(':');
                                const potentialType = description.slice(0, colonIdx).trim();
                                // Only use it as type if it's a reasonable clothing type (not too long)
                                if (potentialType.length < 20 && !potentialType.includes('.')) {
                                  type = potentialType;
                                  description = description.slice(colonIdx + 1).trim();
                                }
                              }
                              
                              allItems.push({
                                key: `article-${idx}`,
                                type,
                                description,
                                category: 'clothing'
                              });
                            }
                          });
                        }
                        
                        // Then add from clothing array if not duplicates
                        // Note: clothing can be a string (Doe Network) or array (NamUs)
                        if (Array.isArray(caseData.clothing) && caseData.clothing.length > 0) {
                          caseData.clothing.forEach((item, idx) => {
                            let type = 'Clothing';
                            let description = '';
                            
                            if (typeof item === 'string') {
                              // Parse "Type: Description" format
                              if (item.includes(':')) {
                                const parts = item.split(':');
                                type = parts[0].trim();
                                description = parts.slice(1).join(':').trim();
                              } else {
                                description = item;
                              }
                            } else if (item?.description) {
                              type = item.type || 'Clothing';
                              description = item.description;
                            }
                            
                            const descKey = normalizeForDedup(description);
                            if (descKey && !seenDescriptions.has(descKey)) {
                              seenDescriptions.add(descKey);
                              allItems.push({
                                key: `clothing-${idx}`,
                                type,
                                description: cleanText(description),
                                category: 'clothing'
                              });
                            }
                          });
                        }
                        
                        // Add accessories
                        if (caseData.accessories?.length > 0) {
                          caseData.accessories.forEach((item, idx) => {
                            let type = 'Accessory';
                            let description = '';
                            
                            if (typeof item === 'string') {
                              if (item.includes(':')) {
                                const parts = item.split(':');
                                type = parts[0].trim();
                                description = parts.slice(1).join(':').trim();
                              } else {
                                description = item;
                              }
                            } else if (item?.description) {
                              type = item.type || 'Accessory';
                              description = item.description;
                            }
                            
                            const descKey = description?.toLowerCase().trim() || '';
                            if (descKey && !seenDescriptions.has(descKey)) {
                              seenDescriptions.add(descKey);
                              allItems.push({
                                key: `accessory-${idx}`,
                                type,
                                description,
                                category: 'accessory'
                              });
                            }
                          });
                        }
                        
                        // Render all unique items
                        return allItems.map((item) => (
                          <div key={item.key} className="flex gap-2 items-start">
                            <Badge 
                              variant="outline" 
                              className={`shrink-0 text-xs ${item.category === 'accessory' ? 'border-amber-300 text-amber-700' : 'border-blue-300 text-blue-700'}`}
                            >
                              {item.type}
                            </Badge>
                            <span className="text-sm text-muted-foreground">{item.description}</span>
                          </div>
                        ));
                      })()}
                      {/* Fallback to description if no structured data */}
                      {caseData.clothing_description && !caseData.clothing_articles?.length && !Array.isArray(caseData.clothing) && (
                        <p className="text-muted-foreground">{caseData.clothing_description}</p>
                      )}
                      {/* Handle string clothing (Doe Network format) */}
                      {typeof caseData.clothing === 'string' && caseData.clothing && !caseData.clothing_articles?.length && (
                        <p className="text-muted-foreground">{caseData.clothing}</p>
                      )}
                      {/* Handle last_seen_clothing (extracted from Charley Project) */}
                      {caseData.last_seen_clothing && !caseData.clothing_articles?.length && !Array.isArray(caseData.clothing) && (
                        <div className="space-y-2">
                          {caseData.last_seen_clothing.split('; ').map((item, idx) => (
                            <div key={`lsc-${idx}`} className="flex gap-2 items-start">
                              <Badge 
                                variant="outline" 
                                className="shrink-0 text-xs border-blue-300 text-blue-700 dark:border-blue-600 dark:text-blue-400"
                              >
                                {item.toLowerCase().includes('glass') ? 'Eyewear' : 
                                 item.toLowerCase().includes('watch') ? 'Watch' :
                                 item.toLowerCase().includes('necklace') || item.toLowerCase().includes('ring') || item.toLowerCase().includes('bracelet') || item.toLowerCase().includes('earring') ? 'Jewelry' :
                                 item.toLowerCase().includes('hat') || item.toLowerCase().includes('cap') ? 'Hat' :
                                 item.toLowerCase().includes('hearing') ? 'Medical' :
                                 'Clothing'}
                              </Badge>
                              <span className="text-sm text-muted-foreground">{item}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    )}
                  </div>
                )}

                {/* Nicknames/Aliases */}
                {caseData.nicknames && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Also Known As:</span>
                    <Badge variant="secondary">{caseData.nicknames}</Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Upgrade Nudge - After main case content */}
            <CaseDetailNudge hasStrongMetadata={!!(caseData.photo_url || caseData.latitude || caseData.age)} />

            {/* Case Timeline */}
            {timelineEvents.length > 0 && (
              <Card className="border-amber-200 dark:border-amber-800">
                <CardHeader className="bg-gradient-to-r from-amber-50 to-white dark:from-amber-950/30 dark:to-slate-900">
                  <CardTitle className="font-heading flex items-center gap-2 text-amber-700 dark:text-amber-400">
                    <History className="w-5 h-5" />
                    Case Timeline
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-amber-200 dark:bg-amber-800" />
                    
                    {/* Timeline events */}
                    <div className="space-y-4">
                      {timelineEvents.map((event, idx) => (
                        <div key={idx} className="relative pl-8">
                          {/* Timeline dot */}
                          <div className="absolute left-1.5 top-1.5 w-3 h-3 rounded-full bg-amber-500 border-2 border-white dark:border-slate-900" />
                          
                          <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-3">
                            {/* Event type label */}
                            <div className="font-semibold text-amber-800 dark:text-amber-300 mb-1">
                              {event.event}
                            </div>
                            
                            {/* Date */}
                            {event.date && (
                              <div className="flex items-center gap-2 mb-2">
                                <Calendar className="w-3 h-3 text-amber-600" />
                                <span className="text-sm text-muted-foreground">
                                  {event.date}
                                </span>
                              </div>
                            )}
                            
                            {/* Description (if available) */}
                            {event.description && (
                              <p className="text-sm text-muted-foreground">{event.description}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Data Sources Section - For merged/canonical cases - Moved below Timeline */}
            {caseData?.is_canonical && (
              <div id="merged-sources-section">
                <SourcesTab caseId={id} isCanonical={caseData?.is_canonical} />
              </div>
            )}

            {/* Vehicle Information */}
            {(caseData.vehicle_info || (caseData.vehicles && caseData.vehicles.length > 0) || caseData.associated_vehicle) && (
              <Card className="border-green-200 dark:border-green-800">
                <CardHeader className="bg-gradient-to-r from-green-50 to-white dark:from-green-950/30 dark:to-slate-900">
                  <CardTitle className="font-heading flex items-center gap-2 text-green-700 dark:text-green-400">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L18 10l-4-6H8L4 10l-2.5.1c-.8.2-1.5 1-1.5 1.9v3c0 .6.4 1 1 1h2"/>
                      <circle cx="7" cy="17" r="2"/>
                      <path d="M9 17h6"/>
                      <circle cx="17" cy="17" r="2"/>
                    </svg>
                    Associated Vehicle(s)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  {/* Text description of vehicle (from Charley Project) */}
                  {caseData.associated_vehicle && (
                    <p className="text-muted-foreground mb-4">{caseData.associated_vehicle}</p>
                  )}
                  {/* Structured vehicle info (from NamUs) */}
                  {(caseData.vehicles || (caseData.vehicle_info ? [caseData.vehicle_info] : [])).filter(Boolean).length > 0 && (
                    <div className="space-y-4">
                      {(caseData.vehicles || [caseData.vehicle_info]).filter(Boolean).map((vehicle, idx) => (
                      <div key={idx} className="bg-green-50 dark:bg-green-950/30 rounded-lg p-4">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {vehicle.year && (
                            <div className="flex flex-col">
                              <span className="text-xs text-muted-foreground uppercase tracking-wide">Year</span>
                              <span className="font-medium">{vehicle.year}</span>
                            </div>
                          )}
                          {vehicle.make && (
                            <div className="flex flex-col">
                              <span className="text-xs text-muted-foreground uppercase tracking-wide">Make</span>
                              <span className="font-medium">{vehicle.make}</span>
                            </div>
                          )}
                          {vehicle.model && (
                            <div className="flex flex-col">
                              <span className="text-xs text-muted-foreground uppercase tracking-wide">Model</span>
                              <span className="font-medium">{vehicle.model}</span>
                            </div>
                          )}
                          {vehicle.color && (
                            <div className="flex flex-col">
                              <span className="text-xs text-muted-foreground uppercase tracking-wide">Color</span>
                              <span className="font-medium">{vehicle.color}</span>
                            </div>
                          )}
                          {vehicle.style && (
                            <div className="flex flex-col">
                              <span className="text-xs text-muted-foreground uppercase tracking-wide">Style</span>
                              <span className="font-medium">{vehicle.style}</span>
                            </div>
                          )}
                          {vehicle.license_plate && (
                            <div className="flex flex-col">
                              <span className="text-xs text-muted-foreground uppercase tracking-wide">License Plate</span>
                              <span className="font-medium">{vehicle.license_plate} {vehicle.license_state && `(${vehicle.license_state})`}</span>
                            </div>
                          )}
                        </div>
                        {vehicle.comment && (
                          <p className="mt-2 text-sm text-muted-foreground italic">{vehicle.comment}</p>
                        )}
                      </div>
                    ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Medical Conditions */}
            {caseData.medical_conditions && (
              <Card className="border-red-200 dark:border-red-800">
                <CardHeader className="bg-gradient-to-r from-red-50 to-white dark:from-red-950/30 dark:to-slate-900">
                  <CardTitle className="font-heading flex items-center gap-2 text-red-700 dark:text-red-400">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                    </svg>
                    Medical Conditions
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <p className="text-muted-foreground">{caseData.medical_conditions}</p>
                </CardContent>
              </Card>
            )}

            {/* Investigating Agency */}
            {(caseData.investigating_agency || (caseData.investigating_agencies && caseData.investigating_agencies.length > 0)) && (
              <Card className="border-trust-blue-200 dark:border-trust-blue-800">
                <CardHeader className="bg-gradient-to-r from-trust-blue-50 to-white dark:from-trust-blue-950/30 dark:to-slate-900">
                  <CardTitle className="font-heading flex items-center gap-2 text-trust-blue-700 dark:text-trust-blue-400">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10 9 9 9 8 9"/>
                    </svg>
                    Investigating Agency
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-4">
                    {(caseData.investigating_agencies || [caseData.investigating_agency]).filter(Boolean).map((agency, idx) => {
                      // Handle both string format (FBI) and object format (NamUs)
                      const isString = typeof agency === 'string';
                      const agencyName = isString ? agency : agency.name;
                      const agencyCity = isString ? null : agency.city;
                      const agencyState = isString ? null : agency.state;
                      const agencyCounty = isString ? null : agency.county;
                      const agencyPhone = isString ? null : agency.phone;
                      const caseNumber = isString ? null : agency.case_number;
                      
                      // Determine if it's an FBI field office
                      const isFBI = agencyName && agencyName.toLowerCase().includes('fbi');
                      
                      return (
                        <div key={idx} className={`rounded-lg p-4 ${isFBI ? 'bg-red-50 dark:bg-red-950/30' : 'bg-trust-blue-50 dark:bg-trust-blue-950/30'}`}>
                          <h4 className={`font-semibold mb-2 ${isFBI ? 'text-red-700 dark:text-red-400' : 'text-trust-blue-700 dark:text-trust-blue-400'}`}>
                            {isFBI && (
                              <span className="inline-flex items-center gap-2">
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 2L2 7v10l10 5 10-5V7L12 2zm0 2.18l6.9 3.45L12 11.09 5.1 7.63 12 4.18zM4 8.82l7 3.5v7.36l-7-3.5V8.82zm9 10.86v-7.36l7-3.5v7.36l-7 3.5z"/>
                                </svg>
                                {agencyName}
                              </span>
                            )}
                            {!isFBI && agencyName}
                          </h4>
                          {!isString && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                              {(agencyCity || agencyState) && (
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-muted-foreground" />
                                  <span>{[agencyCity, agencyCounty, agencyState].filter(Boolean).join(', ')}</span>
                                </div>
                              )}
                              {agencyPhone && (
                                <div className="flex items-center gap-2">
                                  <Phone className="w-4 h-4 text-muted-foreground" />
                                  <a href={`tel:${agencyPhone}`} className="text-trust-blue-600 hover:underline">
                                    {agencyPhone.toString().replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}
                                  </a>
                                </div>
                              )}
                              {caseNumber && (
                                <div className="flex items-center gap-2">
                                  <span className="text-muted-foreground">Case #:</span>
                                  <span className="font-mono">{caseNumber}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {caseData.case_number && !caseData.investigating_agencies?.some(a => a.case_number) && (
                    <div className="mt-3 flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">Agency Case Number:</span>
                      <span className="font-mono font-medium">{caseData.case_number}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* ========== EXTRACTED INTELLIGENCE SECTION ========== */}
            {/* This section is visually distinct from official case data */}
            <ExtractedIntelligence 
              items={extractedIntel}
              sources={intelSources}
              isAdmin={user?.is_admin}
              loading={intelLoading}
            />

            {/* Case Network Section - DESKTOP ONLY (mobile version in sidebar after tips) */}
            <div className="hidden lg:block">
              <CaseNetworkSection 
                caseId={id}
                caseName={caseData.name || 'Unknown'}
                caseType={caseData.case_type}
              />
            </div>

            {/* Case Updates Section */}
            {latestUpdate && (
              <LatestUpdateCard 
                update={latestUpdate} 
                onViewAll={() => setShowAllUpdates(true)} 
              />
            )}

            {/* Key Developments */}
            {caseUpdates.length > 0 && (
              <div className="flex flex-wrap gap-2 my-2">
                <KeyDevelopmentsChips updates={caseUpdates} />
              </div>
            )}

            {/* Full Updates Timeline */}
            {showAllUpdates && caseUpdates.length > 0 && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-heading flex items-center gap-2">
                    <History className="w-5 h-5 text-trust-blue-500" />
                    Case Updates Timeline
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {user?.is_admin && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setShowAddUpdate(true)}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Update
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setShowAllUpdates(false)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <UpdatesTimeline updates={caseUpdates} maxVisible={10} />
                </CardContent>
              </Card>
            )}

            {/* Add Update Form Modal */}
            {showAddUpdate && user?.is_admin && (
              <Card className="border-trust-blue-300 shadow-lg">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Plus className="w-5 h-5 text-trust-blue-500" />
                    Add Case Update
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <AddCaseUpdateForm 
                    caseId={id}
                    caseName={caseData.name}
                    onSuccess={(newUpdate) => {
                      setShowAddUpdate(false);
                      fetchCaseUpdates();
                      toast.success('Update added successfully');
                    }}
                    onCancel={() => setShowAddUpdate(false)}
                  />
                </CardContent>
              </Card>
            )}

            {/* Admin: Add Update Button (when no updates exist yet) */}
            {user?.is_admin && !latestUpdate && !showAddUpdate && (
              <Card className="border-dashed border-2">
                <CardContent className="py-6 text-center">
                  <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground mb-3">No updates yet for this case</p>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAddUpdate(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Update
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Age Progression AI - DESKTOP ONLY (mobile version is above after timeline) */}
            <Card className="hidden lg:block border-purple-200 dark:border-purple-800 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30">
                <CardTitle className="font-heading flex items-center gap-2 text-purple-700 dark:text-purple-400">
                  <Wand2 className="w-5 h-5" />
                  AI Age Progression
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {ageProgressionAccess === 'guest' || ageProgressionAccess === 'free' || (!user && !ageProgressionAccess) ? (
                  <div className="text-center py-4">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/50 mb-3">
                      <Lock className="w-6 h-6 text-purple-600" />
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      {!user ? 'Sign in to generate age progressions' : 'Upgrade to Pro to use AI age progression'}
                    </p>
                    <Link to={user ? "/pricing" : "/login"}>
                      <Button size="sm" className="bg-purple-600 hover:bg-purple-700 rounded-full">
                        {user ? 'Upgrade to Pro' : 'Sign In'}
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </Link>
                  </div>
                ) : showAgeProgression && ageProgressions.length > 0 ? (
                  <div className="space-y-4">
                    {(() => {
                      const plan = computeProgressionPlan();
                      const lastSeenYear = plan.lastSeenYear;
                      const originalPhoto = caseData?.photo_url
                        || (Array.isArray(caseData?.all_photos) && caseData.all_photos[0])
                        || null;

                      return (
                        <>
                          <p className="text-sm text-muted-foreground">
                            AI-generated projections of how this person may look
                            today, based on aging patterns
                            {plan.totalYearsMissing != null
                              ? ` over the ${plan.totalYearsMissing} years since they were last seen.`
                              : '.'}
                          </p>

                          {ageProgressions.map((prog, idx) => {
                            const targetYear = lastSeenYear
                              ? lastSeenYear + (prog.years_added || 0)
                              : null;
                            const isSkinFail = prog.failure_code === 'SKIN_TONE_MISMATCH';
                            const isOtherFail = prog.error && !isSkinFail;
                            return (
                              <div
                                key={idx}
                                className={`border-2 rounded-lg p-3 ${
                                  isSkinFail
                                    ? 'border-rose-300 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/30'
                                    : 'border-purple-200 dark:border-purple-800 bg-white dark:bg-slate-900/40'
                                }`}
                                data-testid={`age-progression-pair-${prog.years_added}`}
                              >
                                <div className="grid grid-cols-2 gap-3">
                                  {/* Original */}
                                  <div className="text-center">
                                    {originalPhoto ? (
                                      <img
                                        src={originalPhoto}
                                        alt={`Original photo${lastSeenYear ? ` from ${lastSeenYear}` : ''}`}
                                        className="aspect-square w-full object-cover rounded-md border border-slate-200 dark:border-slate-700"
                                      />
                                    ) : (
                                      <div className="aspect-square w-full bg-slate-100 dark:bg-slate-800 rounded-md flex items-center justify-center">
                                        <ImageIcon className="w-8 h-8 text-slate-400" />
                                      </div>
                                    )}
                                    <Badge className="mt-2 bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                                      {lastSeenYear ? `Last seen ${lastSeenYear}` : 'Last seen'}
                                    </Badge>
                                  </div>
                                  {/* Progression */}
                                  <div className="text-center">
                                    {isSkinFail ? (
                                      <div
                                        className="aspect-square w-full bg-rose-100 dark:bg-rose-900/40 rounded-md flex flex-col items-center justify-center text-xs text-rose-800 dark:text-rose-200 px-3 gap-2 border-2 border-rose-300 dark:border-rose-700"
                                        data-testid={`progression-failed-${prog.years_added}`}
                                      >
                                        <ShieldAlert className="w-6 h-6" />
                                        <div className="font-semibold">
                                          Generation rejected
                                        </div>
                                        <div className="text-[10px] leading-tight">
                                          Skin tone did not match the source photo
                                          (ΔE&nbsp;
                                          {prog.validation?.delta_e ?? '?'})
                                          — image suppressed.
                                        </div>
                                      </div>
                                    ) : isOtherFail ? (
                                      <div className="aspect-square w-full bg-amber-50 dark:bg-amber-950/30 rounded-md flex items-center justify-center text-xs text-amber-700 dark:text-amber-300 px-3">
                                        Failed to generate
                                      </div>
                                    ) : (
                                      <img
                                        src={prog.image_base64}
                                        alt={`AI age progression +${prog.years_added} years${targetYear ? ` (${targetYear})` : ''}`}
                                        className="aspect-square w-full object-cover rounded-md border-2 border-purple-300 dark:border-purple-700"
                                      />
                                    )}
                                    <Badge className={`mt-2 ${
                                      isSkinFail
                                        ? 'bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-200'
                                        : 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                                    }`}>
                                      {targetYear ? `Estimated ${targetYear}` : `+${prog.years_added} years`}
                                      {prog.estimated_age && ` · ~${prog.estimated_age}yo`}
                                    </Badge>
                                  </div>
                                </div>

                                <p className="text-[11px] text-muted-foreground italic text-center mt-2 leading-snug">
                                  AI-estimated appearance for public awareness only —
                                  not a forensic reconstruction. Results may vary.
                                </p>

                                {/* Per-progression action row: Download +
                                    Report. Hidden on rejected renders
                                    since there's nothing to download or
                                    re-flag. */}
                                {!isSkinFail && !isOtherFail && (
                                  <div className="mt-2 flex items-center justify-center gap-2 flex-wrap">
                                    <Button
                                      onClick={() => handleDownloadProgression(prog)}
                                      variant="ghost"
                                      size="sm"
                                      className="text-[11px] text-purple-700 hover:bg-purple-50 dark:text-purple-300 dark:hover:bg-purple-950/40 h-7 px-2"
                                      data-testid={`download-progression-${prog.years_added}`}
                                    >
                                      <Download className="w-3 h-3 mr-1" />
                                      Download image
                                    </Button>
                                    <Button
                                      onClick={() => handleReportProgression(
                                        'race_mismatch', prog.years_added
                                      )}
                                      variant="ghost"
                                      size="sm"
                                      className="text-[11px] text-rose-700 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-950/40 h-7 px-2"
                                      data-testid={`report-progression-${prog.years_added}`}
                                    >
                                      <ShieldAlert className="w-3 h-3 mr-1" />
                                      Report incorrect progression
                                    </Button>
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {/* Share-the-progression bar — only when we have at
                              least one successful render. Routes through
                              /api/c/{id}?ap=1 so the OG card swaps in the AI
                              projection on FB / Twitter / LinkedIn. */}
                          {ageProgressions.some((p) => !p.error && p.image_base64) && (
                            <CaseShareBar
                              caseData={caseData}
                              caseId={id}
                              progression={true}
                              tone="progression"
                              title="Share age progression"
                              subtitle="Branded share with AI projection on the preview card — "
                            />
                          )}

                          <Button
                            onClick={handleGenerateAgeProgression}
                            disabled={ageProgressionLoading}
                            variant="outline"
                            size="sm"
                            className="w-full rounded-full border-purple-200 text-purple-600 hover:bg-purple-50"
                          >
                            {ageProgressionLoading ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Regenerating...
                              </>
                            ) : (
                              <>
                                <Wand2 className="w-4 h-4 mr-2" />
                                Regenerate Progressions
                              </>
                            )}
                          </Button>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/50 mb-3">
                      <Wand2 className="w-6 h-6 text-purple-600" />
                    </div>
                    {(() => {
                      const plan = computeProgressionPlan();
                      const ys = plan.years.map((y) => `+${y}`).join(', ');
                      return (
                        <p className="text-sm text-muted-foreground mb-3">
                          Generate AI-powered age progressions to see how this
                          person may look today
                          {plan.totalYearsMissing != null
                            ? ` (${ys} years from their last photo, based on the ${plan.totalYearsMissing}-year window since they were last seen).`
                            : ' (+5, +10, and +15 years from their last photo).'}
                        </p>
                      );
                    })()}
                    <Button
                      onClick={handleGenerateAgeProgression}
                      disabled={ageProgressionLoading}
                      className="bg-purple-600 hover:bg-purple-700 rounded-full"
                    >
                      {ageProgressionLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating (may take 1-2 min)...
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-4 h-4 mr-2" />
                          Generate Age Progressions
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* AI Summary */}
            {aiSummary && (
              <Card className="bg-trust-blue-50 dark:bg-trust-blue-950/30 border-trust-blue-200 dark:border-trust-blue-800">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2 text-trust-blue-700 dark:text-trust-blue-400">
                    <Sparkles className="w-5 h-5" />
                    AI-Generated Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-trust-blue-700 dark:text-trust-blue-300">{aiSummary}</p>
                </CardContent>
              </Card>
            )}

            {/* Map */}
            {(caseData.latitude && caseData.longitude) || extractedLocation ? (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading">Last Known Location</CardTitle>
                  {extractedLocation?.source === 'geocoded' && (
                    <p className="text-sm text-muted-foreground">
                      📍 Location extracted from case description: {extractedLocation.locationName}
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  {locationLoading ? (
                    <div className="h-[400px] flex items-center justify-center bg-muted rounded-lg">
                      <div className="text-center">
                        <div className="animate-spin h-8 w-8 border-2 border-trust-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                        <p className="text-sm text-muted-foreground">Extracting location from description...</p>
                      </div>
                    </div>
                  ) : (
                    <MapComponent 
                      cases={[{
                        ...caseData,
                        latitude: caseData.latitude || extractedLocation?.latitude,
                        longitude: caseData.longitude || extractedLocation?.longitude
                      }]} 
                      center={[
                        caseData.latitude || extractedLocation?.latitude,
                        caseData.longitude || extractedLocation?.longitude
                      ]}
                      zoom={13}
                      height="400px"
                    />
                  )}
                </CardContent>
              </Card>
            ) : locationLoading ? (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading">Last Known Location</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[400px] flex items-center justify-center bg-muted rounded-lg">
                    <div className="text-center">
                      <div className="animate-spin h-8 w-8 border-2 border-trust-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                      <p className="text-sm text-muted-foreground">Extracting location from description...</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : null}

            {/* Genealogy Records - DESKTOP, appears at the very bottom of the case
                page (below the map) per UX: the section is "Coming Soon" so it
                shouldn't sit between primary investigative content. */}
            <div className="hidden lg:block">
              <CaseGenealogySection 
                caseId={id}
                caseName={caseData.name}
                caseData={caseData}
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Tips Count */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-safety-orange-500" />
                  Tips Received
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-safety-orange-500 mb-2">{tips.length}</div>
                <p className="text-sm text-muted-foreground mb-4">tips submitted for this case</p>
                <Link to={`/tip/${caseData.id}`}>
                  <Button className="w-full rounded-full bg-safety-orange-500 hover:bg-safety-orange-600">
                    Submit a Tip
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* MOBILE ONLY: Case Network - Shows after tips on mobile */}
            <div className="lg:hidden">
              <CaseNetworkSection 
                caseId={id}
                caseName={caseData.name || 'Unknown'}
                caseType={caseData.case_type}
              />
            </div>

            {/* MOBILE ONLY: Genealogy Records - Bottom of mobile sidebar
                (Coming Soon — placed last so it doesn't crowd primary content) */}
            <div className="lg:hidden">
              <CaseGenealogySection 
                caseId={id}
                caseName={caseData.name}
                caseData={caseData}
              />
            </div>

            {/* MOBILE ONLY: Matches Section - Shows after network on mobile */}
            <div className="lg:hidden">
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-500" />
                    {(caseData.case_type === 'unidentified' || 
                      caseData.case_type === 'unidentified_person' ||
                      caseData.source === 'namus_unidentified' ||
                      caseData.source === 'doenetwork')
                      ? 'Potential Missing Person Matches'
                      : (caseData.case_type === 'unclaimed_person' || 
                         caseData.case_type === 'unclaimed' ||
                         caseData.source === 'namus_unclaimed')
                        ? 'Potential Missing Person Matches'
                        : 'Match to Unidentified/Unclaimed'}
                    {!isPro && (
                      <Badge variant="outline" className="ml-2 text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border-purple-200">
                        <Lock className="w-3 h-3 mr-1" />
                        Pro
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isPro ? (
                    <>
                      <p className="text-sm text-muted-foreground mb-4">
                        {(caseData.case_type === 'unidentified' || 
                          caseData.case_type === 'unidentified_person' ||
                          caseData.source === 'namus_unidentified' ||
                          caseData.source === 'doenetwork' ||
                          caseData.case_type === 'unclaimed_person' ||
                          caseData.case_type === 'unclaimed' ||
                          caseData.source === 'namus_unclaimed')
                          ? 'Search for missing persons who may match this case based on demographics, location, and timeline.'
                          : 'Search for unidentified or unclaimed persons who may match this missing person.'}
                      </p>
                      <Button 
                        onClick={handleFindSimilar}
                        disabled={loadingAI}
                        className="w-full rounded-full bg-purple-500 hover:bg-purple-600"
                        data-testid="find-similar-btn-mobile"
                      >
                        {loadingAI ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4 mr-2" />
                        )}
                        {(caseData.case_type === 'unidentified' || 
                          caseData.case_type === 'unidentified_person' ||
                          caseData.source === 'namus_unidentified' ||
                          caseData.source === 'doenetwork' ||
                          caseData.case_type === 'unclaimed_person' ||
                          caseData.case_type === 'unclaimed' ||
                          caseData.source === 'namus_unclaimed')
                          ? 'Find Matching Missing Persons'
                          : 'Find Matching Unidentified'}
                      </Button>
                      {/* Results panel — mobile. The desktop sidebar card renders
                          its own copy of renderSimilarResultsBlock() so the user
                          always sees output regardless of viewport. */}
                      {renderSimilarResultsBlock()}
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/50 mb-3">
                        <Lock className="w-6 h-6 text-purple-600" />
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        Upgrade to Pro to find potential matches using AI similarity search.
                      </p>
                      <Link to="/pricing">
                        <Button size="sm" className="bg-purple-600 hover:bg-purple-700 rounded-full">
                          Upgrade to Pro
                          <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* MOBILE ONLY: Age Progression - Shows after matches on mobile */}
            <div className="lg:hidden">
              <Card className="border-purple-200 dark:border-purple-800 overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30">
                  <CardTitle className="font-heading flex items-center gap-2 text-purple-700 dark:text-purple-400">
                    <Wand2 className="w-5 h-5" />
                    AI Age Progression
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  {ageProgressionAccess === 'guest' || ageProgressionAccess === 'free' || (!user && !ageProgressionAccess) ? (
                    <div className="text-center py-4">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/50 mb-3">
                        <Lock className="w-6 h-6 text-purple-600" />
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        {!user ? 'Sign in to generate age progressions' : 'Upgrade to Pro to use AI age progression'}
                      </p>
                      <Link to={user ? "/pricing" : "/login"}>
                        <Button size="sm" className="bg-purple-600 hover:bg-purple-700 rounded-full">
                          {user ? 'Upgrade to Pro' : 'Sign In'}
                          <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  ) : showAgeProgression && ageProgressions.length > 0 ? (
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        AI-generated projections of how this person may look today.
                      </p>
                      <div className="grid grid-cols-3 gap-3">
                        {(() => {
                          const planSide = computeProgressionPlan();
                          return ageProgressions.map((prog, idx) => {
                            const targetYear = planSide.lastSeenYear
                              ? planSide.lastSeenYear + (prog.years_added || 0)
                              : null;
                            return (
                              <div key={idx} className="text-center">
                                {prog.error ? (
                                  <div className="aspect-square bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                                    <ImageIcon className="w-8 h-8 text-gray-400" />
                                  </div>
                                ) : (
                                  <img
                                    src={prog.image_base64}
                                    alt={`+${prog.years_added} years${targetYear ? ` (~${targetYear})` : ''}`}
                                    className="aspect-square object-cover rounded-lg border-2 border-purple-200 dark:border-purple-800"
                                  />
                                )}
                                <Badge className="mt-2 bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 text-xs">
                                  {targetYear ? `~${targetYear}` : `+${prog.years_added}y`}
                                </Badge>
                              </div>
                            );
                          });
                        })()}
                      </div>
                      <p className="text-[11px] text-muted-foreground italic leading-snug">
                        AI-estimated appearance for public awareness only —
                        not a forensic reconstruction. Results may vary.
                      </p>
                      <Button 
                        onClick={handleGenerateAgeProgression}
                        disabled={ageProgressionLoading}
                        variant="outline"
                        size="sm"
                        className="w-full rounded-full border-purple-200 text-purple-600 hover:bg-purple-50"
                      >
                        {ageProgressionLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Regenerating...
                          </>
                        ) : (
                          <>
                            <Wand2 className="w-4 h-4 mr-2" />
                            Regenerate
                          </>
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/50 mb-3">
                        <Wand2 className="w-6 h-6 text-purple-600" />
                      </div>
                      {(() => {
                        const planSide = computeProgressionPlan();
                        const ys = planSide.years.map((y) => `+${y}`).join(', ');
                        return (
                          <p className="text-sm text-muted-foreground mb-3">
                            Generate AI age progressions ({ys} years)
                          </p>
                        );
                      })()}
                      <Button 
                        onClick={handleGenerateAgeProgression}
                        disabled={ageProgressionLoading}
                        className="bg-purple-600 hover:bg-purple-700 rounded-full"
                      >
                        {ageProgressionLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Wand2 className="w-4 h-4 mr-2" />
                            Generate
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* AI Similarity Search - Pro Feature - DESKTOP ONLY (mobile version is in main content after timeline) */}
            <Card className="hidden lg:block">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  {/* Dynamic title based on case type */}
                  {(caseData.case_type === 'unidentified' || 
                    caseData.case_type === 'unidentified_person' ||
                    caseData.source === 'namus_unidentified' ||
                    caseData.source === 'doenetwork')
                    ? 'Potential Missing Person Matches'
                    : (caseData.case_type === 'unclaimed_person' || 
                       caseData.case_type === 'unclaimed' ||
                       caseData.source === 'namus_unclaimed')
                      ? 'Potential Missing Person Matches'
                      : 'Match to Unidentified/Unclaimed'}
                  {!isPro && (
                    <Badge variant="outline" className="ml-2 text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border-purple-200">
                      <Lock className="w-3 h-3 mr-1" />
                      Pro
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isPro ? (
                  <>
                    <p className="text-sm text-muted-foreground mb-4">
                      {(caseData.case_type === 'unidentified' || 
                        caseData.case_type === 'unidentified_person' ||
                        caseData.source === 'namus_unidentified' ||
                        caseData.source === 'doenetwork' ||
                        caseData.case_type === 'unclaimed_person' ||
                        caseData.case_type === 'unclaimed' ||
                        caseData.source === 'namus_unclaimed')
                        ? 'Search for missing persons who may match this case based on demographics, location, and timeline.'
                        : 'Search for unidentified or unclaimed persons who may match this missing person.'}
                    </p>
                    <Button 
                      onClick={handleFindSimilar}
                      disabled={loadingAI}
                      className="w-full rounded-full bg-purple-500 hover:bg-purple-600"
                      data-testid="find-similar-btn"
                    >
                      {loadingAI ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      {(caseData.case_type === 'unidentified' || 
                        caseData.case_type === 'unidentified_person' ||
                        caseData.source === 'namus_unidentified' ||
                        caseData.source === 'doenetwork' ||
                        caseData.case_type === 'unclaimed_person' ||
                        caseData.case_type === 'unclaimed' ||
                        caseData.source === 'namus_unclaimed')
                        ? 'Find Matching Missing Persons'
                        : 'Find Matching Unidentified'}
                    </Button>

                    {/* Distinctive features + results — single source of truth
                        via renderSimilarResultsBlock(). The mobile copy of this
                        card (further down) renders the same block. */}
                    {renderSimilarResultsBlock()}
                  </>
                ) : (
                  /* Free tier users see limited matches */
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      {(caseData.case_type === 'unidentified' || 
                        caseData.case_type === 'unidentified_person' ||
                        caseData.source === 'namus_unidentified' ||
                        caseData.source === 'doenetwork' ||
                        caseData.case_type === 'unclaimed_person' ||
                        caseData.case_type === 'unclaimed' ||
                        caseData.source === 'namus_unclaimed')
                        ? 'Top potential matches for this case based on demographics, location, and timeline.'
                        : 'Top potential matches based on demographics, location, and timeline.'}
                    </p>
                    
                    {/* Show top 3 matches as preview, then deep-link to the Workbench tab for all 10 */}
                    {potentialMatches.length > 0 ? (
                      <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                          Top {Math.min(3, potentialMatches.length)} Matches
                          <Badge variant="outline" className="text-xs bg-green-50 dark:bg-green-900/30 text-green-600 border-green-200">
                            Preview
                          </Badge>
                        </h4>
                        
                        {potentialMatches.slice(0, 3).map((match) => (
                          <Card key={match.id} className="p-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                            <div className="flex gap-3">
                              <MatchThumb
                                photoUrl={
                                  match.missing_person_photo_url ||
                                  match.unidentified_person_photo_url ||
                                  match.missing_person_photo ||
                                  match.unidentified_person_photo
                                }
                                alt={match.missing_person_name || match.unidentified_person_name || 'Match'}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-2 gap-2">
                                  <span className="font-medium text-sm truncate">
                                    {match.missing_person_name || match.unidentified_person_name || 'Unknown'}
                                  </span>
                                  <Badge
                                    variant="secondary"
                                    className={`text-xs shrink-0 ${
                                      match.match_score >= 85 ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' :
                                      match.match_score >= 70 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300' :
                                      'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                                    }`}
                                  >
                                    {Math.round(match.match_score)}% Match
                                  </Badge>
                                </div>

                                {/* Basic signals - free tier can see these */}
                                <div className="text-xs text-muted-foreground space-y-1">
                                  <div className="flex flex-wrap gap-2">
                                    {match.missing_person_state && (
                                      <span className="flex items-center gap-1">
                                        <MapPin className="w-3 h-3" />
                                        {match.missing_person_state}
                                      </span>
                                    )}
                                    {match.unidentified_person_state && (
                                      <span className="flex items-center gap-1">
                                        <MapPin className="w-3 h-3" />
                                        {match.unidentified_person_state}
                                      </span>
                                    )}
                                    {(match.missing_person_age || match.unidentified_person_age) && (
                                      <span className="flex items-center gap-1">
                                        <User className="w-3 h-3" />
                                        Age: {match.missing_person_age || match.unidentified_person_age}
                                      </span>
                                    )}
                                  </div>

                                  {/* Show basic match reasons */}
                                  {match.match_reasons && match.match_reasons.length > 0 && (
                                    <div className="mt-2 p-2 bg-white dark:bg-slate-900 rounded">
                                      {match.match_reasons.slice(0, 2).map((reason, idx) => (
                                        <p key={idx} className="text-xs text-slate-600 dark:text-slate-400">
                                          {idx === 0 ? '✓ ' : '• '}{reason.length > 60 ? reason.substring(0, 60) + '...' : reason}
                                        </p>
                                      ))}
                                    </div>
                                  )}
                                  <div className="mt-2 flex justify-end">
                                    <FlagMatchButton matchId={match.id} size="sm" variant="ghost" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Card>
                        ))}

                        {/* Logged-out: nudge them to create an account so they
                            can see the rest of the ranked candidates. Logged-in
                            users see the existing Workbench CTA below instead. */}
                        {!user && (
                          <SignupCTA variant="match" context={`case-match:${id}`} ctaTestId="match-preview-signup-cta" />
                        )}
                        
                        {/* Workbench engagement CTA — replaces the old upgrade gate.
                            Switches the page to the Workbench tab so users see all
                            10 ranked matches + community research in one surface. */}
                        <button
                          type="button"
                          onClick={() => {
                            setActiveTab('workbench');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="mt-4 w-full p-4 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/20 hover:from-indigo-100 hover:to-purple-100 dark:hover:from-indigo-900/50 dark:hover:to-purple-900/30 transition-colors text-left group"
                          data-testid="view-all-matches-on-workbench"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shrink-0">
                                <Target className="w-5 h-5 text-white" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-slate-900 dark:text-white text-sm sm:text-base mb-0.5">
                                  View all matches on the Workbench
                                </h4>
                                <p className="text-xs text-muted-foreground">
                                  See the top {Math.min(10, totalMatchesAvailable || potentialMatches.length || 10)} ranked candidates,
                                  flag any that warrant a closer look from our team, and add research notes.
                                </p>
                              </div>
                            </div>
                            <ArrowRight className="w-5 h-5 text-indigo-500 shrink-0 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </button>
                      </div>
                    ) : matchesLoading ? (
                      <div className="text-center py-4">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-purple-500" />
                        <p className="text-sm text-muted-foreground mt-2">Loading matches...</p>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
                          <Search className="w-6 h-6 text-slate-400" />
                        </div>
                        <p className="text-sm text-muted-foreground">No matches found for this case yet.</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Community Research Notes (Reddit launch — public write layer) */}
            <CommunityResearchNotes caseId={caseData.id || caseData.external_id || id} />

            {/* Reporter Info */}
            {caseData.organization_name && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <User className="w-5 h-5 text-green-500" />
                    Reported By
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Badge variant="secondary" className="mb-2">
                    {caseData.reporter_type === 'organization' ? 'Verified Organization' : 'Public'}
                  </Badge>
                  <p className="font-medium">{caseData.organization_name}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
        </>
        )}
      </div>

      {/* Flyer Modal */}
      <FlyerModal
        isOpen={showFlyerModal}
        onClose={() => setShowFlyerModal(false)}
        caseData={caseData}
      />
    </div>
  );
}
